// src/components/block/liveStreamManagement/DeckV2.tsx
//
// LAYOUT V2.4 — modifications :
//   • Suppression du titre "Deck" et des boutons header (déplacés dans ControlBar)
//   • TOTAL_COLS passe à 17 (médias +1 col : 2 sons + 2 jingles → 3+3; scènes +1 → 8)
//   • Prop onRegisterPupitreOpen pour exposer l'ouverture du modal pupitre au parent

import React, { useEffect, useState, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Video, Settings, Image, Music, Eye, Volume2, VolumeX, Film, Clapperboard, RefreshCw,
  X, AlertTriangle, Keyboard, Plus,
} from 'lucide-react';
import type { AudioSourceRegistry } from '../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';
import DeckMixerPanel, { DeckMixerStrip } from './DeckMixerPanel';

import {
  VideoSource, AudioOnlyVisual, ImageSlot, SoundSlot, VideoOverlaySlot, JingleSlot, LayoutSlot,
  SLOT_COUNT, SourceAudioConfig,
} from '../../../contexts/MediaStreamContext';
import { DeckKeyMap, KeyBinding, MediaKind, MediaAssignment } from '../../../dataTypes/liveStreamManagment/types_deck';
import {
  makeReorderDragHandlers, swapAt, ReorderFamily, ReorderDragHandlers,
} from '../../../dataTypes/liveStreamManagment/utils_dragReorder';
import { planMediaReorder, applyLibrarySwap } from '../../../dataTypes/liveStreamManagment/utils_deckReorder';
import { formatKeyBinding, findConflict } from '../../../dataTypes/liveStreamManagment/utils_deck';
import {
  useStreamDeckBridge, loadStreamDeckEnabled, saveStreamDeckEnabled,
  StreamDeckCommand, StreamDeckKeyState, StreamDeckSnapshot,
  useStreamDeckPolls, runStreamDeckPollCommand,
  useStreamDeckControls, runStreamDeckControlCommand,
} from '../../../dataTypes/liveStreamManagment/streamDeckBridge';
import { XtreamCredentials } from '../../../dataTypes/liveStreamManagment/sourceRegistry/types';
import { SourceAliasMap, resolveSourceLabel } from './SourceAliasConfigModal';
import PupitreConfigModal from './PupitreConfigModal';
import SourceConfigModal, {
  AudioEffectState, AudioEffectId, StereoVolume,
  DEFAULT_AUDIO_EFFECTS, DEFAULT_STEREO_VOLUME,
} from './SourceConfigModal';
import { VideoEffectsSettings } from './videoEffects/VideoEffectsPipelineManager';
import { SlotVuMeter } from './VuMeter';
import { HlsProbeStatus } from './useHlsProber';
import { Window } from '../../widget/windows/window';
import { ConfigTab } from './ProductionControlRoom_MediaboardConfig';

import Icon from '../../widget/images/Icon'
import TbarSettingsModal from './enchainements/TbarSettingsModal';

// ─────────────────────────────────────────────────────────────────────────────
// Constantes de layout V2.4
// ─────────────────────────────────────────────────────────────────────────────

const V2_VIDEO_VISIBLE  = 8;

// Zone médias : 4 colonnes × 2 lignes, boutons indifférents au type (image,
// son ou jingle — cf. mediaAssignments). Passée de 9 à 4 colonnes le
// 2026-10-20 pour faire place au bloc Mixeur.
const V2_MEDIA_COLS     = 4;
const TOTAL_MEDIA_BTNS  = V2_MEDIA_COLS * 2; // 8

// Scènes : une seule ligne, alignée sous les médias
const SCENE_COLS        = V2_MEDIA_COLS;
const V2_LAYOUT_VISIBLE = SCENE_COLS;        // 4

// Bloc Mixeur : à droite, sur les deux lignes (colonnes libérées par médias et scènes)
const MIXER_COLS = 5;

// Partie boutons : 8 vidéo + 1 tbar + 4 médias = 13 colonnes
// Ligne 2        : 8 preview + 1 cut + 4 scènes = 13 colonnes ✓
const BUTTON_COLS = V2_VIDEO_VISIBLE + 1 + V2_MEDIA_COLS; // 13

// Grille totale : 13 + 5 Mixeur = 18 colonnes (largeur des boutons inchangée)
const TOTAL_COLS = BUTTON_COLS + MIXER_COLS; // 18

// ── Palette ───────────────────────────────────────────────────────────────────

interface ColorState { led: string; text: string; bg: string; border: string; shadow: string; }
const EMPTY: ColorState = { led: '#334155', text: '#ffffff', bg: '#1e293b', border: '#334155', shadow: 'none' };
const PALETTES = {
  video:   { avail: { led:'#4ade80', text:'#fff', bg:'linear-gradient(135deg,#14532d,#166534)',  border:'#4ade80', shadow:'0 0 8px #4ade8050' },  active:{ led:'#ff4444', text:'#fff', bg:'linear-gradient(135deg,#166534,#22c55e)', border:'#ff4444', shadow:'0 0 12px #86efac80' } },
  preview: { avail: { led:'#38bdf8', text:'#fff', bg:'linear-gradient(135deg,#0c4a6e,#075985)',  border:'#38bdf8', shadow:'0 0 8px #38bdf850' },  active:{ led:'#22c55e', text:'#fff', bg:'linear-gradient(135deg,#075985,#0284c7)', border:'#22c55e', shadow:'0 0 12px #7dd3fc80' } },
  image:   { avail: { led:'#22d3ee', text:'#fff', bg:'linear-gradient(135deg,#164e63,#155e75)',  border:'#22d3ee', shadow:'0 0 8px #22d3ee50' },  active:{ led:'#a5f3fc', text:'#fff', bg:'linear-gradient(135deg,#155e75,#06b6d4)', border:'#a5f3fc', shadow:'0 0 12px #a5f3fc80' } },
  sound:   { avail: { led:'#facc15', text:'#fff', bg:'linear-gradient(135deg,#713f12,#854d0e)',  border:'#facc15', shadow:'0 0 8px #facc1550' },  active:{ led:'#fef08a', text:'#fff', bg:'linear-gradient(135deg,#854d0e,#ca8a04)', border:'#fef08a', shadow:'0 0 12px #fef08a80' } },
  pip:     { avail: { led:'#e879f9', text:'#fff', bg:'linear-gradient(135deg,#581c87,#6b21a8)',  border:'#e879f9', shadow:'0 0 8px #e879f950' },  active:{ led:'#f0abfc', text:'#fff', bg:'linear-gradient(135deg,#6b21a8,#a855f7)', border:'#f0abfc', shadow:'0 0 12px #f0abfc80' } },
  jingle:  { avail: { led:'#f87171', text:'#fff', bg:'linear-gradient(135deg,#7f1d1d,#991b1b)',  border:'#f87171', shadow:'0 0 8px #f8717150' },  active:{ led:'#fca5a5', text:'#fff', bg:'linear-gradient(135deg,#991b1b,#ef4444)', border:'#fca5a5', shadow:'0 0 12px #fca5a580' } },
  layout:  { avail: { led:'#60a5fa', text:'#fff', bg:'linear-gradient(135deg,#1e3a5f,#1e40af)',  border:'#60a5fa', shadow:'0 0 8px #60a5fa50' },  active:{ led:'#93c5fd', text:'#fff', bg:'linear-gradient(135deg,#1e40af,#3b82f6)', border:'#93c5fd', shadow:'0 0 12px #93c5fd80' } },
} as const;
type PaletteKey = keyof typeof PALETTES;

const stateOfRow = (row: PaletteKey, has: boolean, active: boolean): ColorState =>
  !has ? EMPTY : active ? PALETTES[row].active : PALETTES[row].avail;

const EMOJI_RE = /^[\p{Emoji_Presentation}\p{Extended_Pictographic}\u200d\ufe0f\s]+/u;
const cleanLabel = (s: string | null | undefined) => (s ?? '').replace(EMOJI_RE, '').trim();

const buildBoxShadow = (shadow: string, extra = 'inset 0 2px 4px rgba(0,0,0,0.4)'): string =>
  shadow === 'none' ? extra : `${shadow}, ${extra}`;

const PROBING_STATE: ColorState = { led: '#94a3b8', text: '#fff', bg: 'linear-gradient(135deg,#1e293b,#334155)', border: '#64748b', shadow: '0 0 4px #64748b30' };
const FAILED_STATE: ColorState  = { led: '#f97316', text: '#fff', bg: 'linear-gradient(135deg,#1c1917,#292524)', border: '#f97316', shadow: '0 0 6px #f9731640' };

const HLS_BLINK_STYLE = `
  @keyframes hls-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
  @keyframes probe-fade { 0%, 100% { opacity: 0.3; } 50% { opacity: 1; } }
`;

// ── Icône Power ───────────────────────────────────────────────────────────────
const PowerIcon: React.FC<{ size?: number }> = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <path d="M7 1.5V7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M4.5 3.2A5 5 0 1 0 9.5 3.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

// ── RowLabel ──────────────────────────────────────────────────────────────────
const RowLabel: React.FC<{ icon?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties }> = ({ icon, children, style }) => (
  <div style={{ display:'flex', alignItems:'center', gap:4, marginBottom:4, ...style }}>
    {icon && <span style={{ display:'flex', alignItems:'center', opacity:0.7 }}>{icon}</span>}
    <span style={{ fontSize:11, fontWeight:600, color:'#ffffff' }}>{children}</span>
  </div>
);

// Épaisseur du liseré d'un bouton actif — en final pour une source vidéo.
//
// Il est dessiné PAR-DESSUS le bouton, pas en bordure. Une bordure participe à la
// boîte : à 6 px, elle rognait l'intérieur du bouton et le contenu (la pastille, le
// libellé) se tassait à chaque passage à l'antenne, quand elle ne débordait pas de sa
// case. La bordure reste donc à 2 px dans tous les états, et c'est ce cadre en
// surimpression qui porte l'épaisseur.
const ACTIVE_FRAME_WIDTH = 6;

// ── DeckButton ────────────────────────────────────────────────────────────────
interface DBProps {
  label: string | null; state: ColorState; isActive?: boolean;
  keyBinding?: string | null; title?: string; disabled?: boolean;
  onClick: () => void; extra?: React.ReactNode;
  height: number;
  onOpenConfig?: () => void;
  onAddNew?: () => void;
  // Réordonnancement au glisser. Absents, le bouton se comporte comme avant.
  dragProps?: ReorderDragHandlers;
  isDragged?: boolean;
  isDropTarget?: boolean;
  /**
   * Rend ce bouton cliquable même quand le pupitre entier a été rendu inerte
   * (régie en observation : `pointer-events: none` posé plus haut). Réservé à ce
   * qui n'agit QUE sur l'écran de celui qui clique.
   */
  interactive?: boolean;
}
const DeckButton: React.FC<DBProps> = ({ label, state, isActive = false, keyBinding, title, disabled, onClick, extra, height, onOpenConfig, onAddNew, dragProps, isDragged, isDropTarget, interactive }) => {
  // Le clic déclenche le bouton, le glisser le déplace. Sans ce garde, relâcher
  // un glisser sur place déclencherait AUSSI le clic — l'opérateur enverrait un
  // média à l'antenne en croyant n'avoir rien fait.
  const draggingRef = useRef(false);
  return (
  <div
    {...(dragProps ?? {})}
    onDragStart={dragProps ? (e) => { draggingRef.current = true; dragProps.onDragStart(e); } : undefined}
    onDragEnd={dragProps ? () => { dragProps.onDragEnd(); setTimeout(() => { draggingRef.current = false; }, 0); } : undefined}
    style={{
      position: 'relative', width: '100%', height, flexShrink: 0,
      borderRadius: 8,
      pointerEvents: interactive ? 'auto' : undefined,
      cursor: dragProps?.draggable ? 'grab' : (interactive ? 'pointer' : undefined),
      opacity: isDragged ? 0.35 : 1,
      // Liseré ambre pour la cible de dépôt : le rouge et le vert disent déjà
      // l'antenne et la preview, et l'opérateur doit voir où le bouton va
      // atterrir sans confondre avec un état de diffusion.
      outline: isDropTarget ? '2px solid rgba(245,158,11,0.9)' : undefined,
      outlineOffset: isDropTarget ? -2 : undefined,
    }}>
    <button onClick={() => { if (!draggingRef.current) onClick(); }} disabled={disabled} title={title} style={{
      width: '100%', height: '100%',
      background: state.bg,
      border: `2px solid ${state.border}`,
      borderRadius: 8, cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      gap: 3, transition: 'all 0.15s',
      boxShadow: buildBoxShadow(state.shadow),
      minWidth: 0,
      // Le contenu rentre de l'épaisseur du cadre d'activité : sans cette marge, le
      // libellé passerait dessous dès que le bouton est actif.
      padding: `${ACTIVE_FRAME_WIDTH}px`,
      // padding bas pour laisser place à l'engrenage (et au + éventuel) sans chevauchement
      paddingBottom: (onOpenConfig || onAddNew) ? 18 : ACTIVE_FRAME_WIDTH,
    }}>
      <div style={{ width:7, height:7, borderRadius:'50%', background:state.led, boxShadow: state === EMPTY ? 'none' : `0 0 6px ${state.led}` }} />
      <span style={{ fontSize:11, fontWeight:700, color:'#fff', textAlign:'center', lineHeight:1.2, padding:'0 3px',
        overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' as React.CSSProperties['WebkitBoxOrient'],
        wordBreak:'break-word', opacity: disabled ? 0.5 : 1 }}>{cleanLabel(label) || '—'}</span>
      {keyBinding && <span style={{ fontSize:9, fontFamily:'monospace', color:'#fff', lineHeight:1 }}>[{keyBinding}]</span>}
      {extra}
    </button>
    {isActive && (
      <div style={{
        position     : 'absolute',
        inset        : 0,
        border       : `${ACTIVE_FRAME_WIDTH}px solid ${state.border}`,
        borderRadius : 8,
        pointerEvents: 'none',
        zIndex       : 1,
      }} />
    )}

    {/* Engrenage (+ éventuel bouton "+" pour affecter un emplacement vide) centrés en bas, comme dans VideoDeckButton */}
    {(onOpenConfig || onAddNew) && (
      <div style={{
        position:'absolute', bottom:0, left:0, right:0, height:18,
        display:'flex', alignItems:'center', justifyContent:'center', gap:4,
        pointerEvents:'none',
      }}>
        {onAddNew && (
          <button
            onClick={e => { e.stopPropagation(); onAddNew(); }}
            title="Affecter cet emplacement"
            style={{
              background:'none', border:'none', cursor:'pointer', padding:0,
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'rgba(255,255,255,0.3)', width:18, height:18, transition:'color 0.15s',
              pointerEvents:'auto',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}
          >
            <Plus style={{ width:11, height:11 }} />
          </button>
        )}
        {onOpenConfig && (
          <button
            onClick={e => { e.stopPropagation(); onOpenConfig(); }}
            title="Configurer"
            style={{
              background:'none', border:'none', cursor:'pointer', padding:0,
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'rgba(255,255,255,0.3)', width:18, height:18, transition:'color 0.15s',
              pointerEvents:'auto',
            }}
            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.75)')}
            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}
          >
            <Settings style={{ width:11, height:11 }} />
          </button>
        )}
      </div>
    )}
  </div>
  );
};

// ── VideoDeckButton ────────────────────────────────────────────────────────────
interface VDBProps {
  label: string | null; state: ColorState; isActive?: boolean; keyBinding?: string | null;
  title?: string; disabled?: boolean; onClick: () => void; sourceId: string;
  // v5.0 — plus de `audioLevel` : le VU s'abonne lui-même via `sourceId`.
  audioCfg: SourceAudioConfig;
  onMuteToggle: () => void;
  onOpenConfig?: () => void; onRetry?: () => void;
  isPoweredOff?: boolean; onTogglePower?: () => void;
  height: number;
  dragProps?: ReorderDragHandlers;
  isDragged?: boolean;
  isDropTarget?: boolean;
}
const VideoDeckButton: React.FC<VDBProps> = ({
  label, state, keyBinding, title, disabled, onClick, isActive = false,
  sourceId, audioCfg, onMuteToggle, onOpenConfig,
  onRetry, isPoweredOff, onTogglePower, height,
  dragProps, isDragged, isDropTarget,
}) => {
  const hasAudio = !disabled;
  // Cf. DeckButton : sans ce garde, un glisser relâché sur place enverrait la
  // source à l'antenne.
  const draggingRef = useRef(false);

  const powerBtn = onTogglePower ? (
    <button
      onClick={e => { e.stopPropagation(); onTogglePower(); }}
      title={isPoweredOff ? 'Réactiver la source' : 'Désactiver la source'}
      style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', justifyContent:'center', color: isPoweredOff ? '#ef4444' : 'rgba(255,255,255,0.35)', width:20, height:20, transition:'color 0.15s' }}
      onMouseEnter={e => (e.currentTarget.style.color = isPoweredOff ? '#fca5a5' : 'rgba(255,255,255,0.7)')}
      onMouseLeave={e => (e.currentTarget.style.color = isPoweredOff ? '#ef4444' : 'rgba(255,255,255,0.35)')}
    ><PowerIcon size={14} /></button>
  ) : null;

  const configBtn = (
    <button
      onClick={e => { e.stopPropagation(); onOpenConfig && onOpenConfig(); }}
      title="Configurer"
      style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', justifyContent:'center', color:'rgba(255,255,255,0.35)', width:20, height:20, transition:'color 0.15s' }}
      onMouseEnter={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.7)')}
      onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.35)')}
    ><Settings style={{ width:14, height:14 }} /></button>
  );

  return (
    <div
      {...(dragProps ?? {})}
      onDragStart={dragProps ? (e) => { draggingRef.current = true; dragProps.onDragStart(e); } : undefined}
      onDragEnd={dragProps ? () => { dragProps.onDragEnd(); setTimeout(() => { draggingRef.current = false; }, 0); } : undefined}
      style={{
      background: state.bg,
      border: `2px solid ${state.border}`,
      borderRadius: 8, overflow: 'hidden',
      // Ancre du cadre d'activité, posé en surimpression en fin de composant.
      position: 'relative',
      display: 'flex', flexDirection: 'column',
      transition: 'all 0.15s',
      boxShadow: buildBoxShadow(state.shadow),
      minWidth: 0, width: '100%',
      height,
      flexShrink: 0,
      cursor: dragProps?.draggable ? 'grab' : undefined,
      opacity: isDragged ? 0.35 : 1,
      outline: isDropTarget ? '2px solid rgba(245,158,11,0.9)' : undefined,
      outlineOffset: isDropTarget ? -2 : undefined,
    }}>
      <style>{HLS_BLINK_STYLE}</style>

      <button onClick={() => { if (!draggingRef.current) onClick(); }} 
	          disabled={disabled} 
			  title={title} 
			  style={{ background:'none', 
			           border:'none', 
					   padding:`${ACTIVE_FRAME_WIDTH + 2}px ${ACTIVE_FRAME_WIDTH + 2}px 4px`, 
					   cursor:disabled?'not-allowed':'pointer', 
					   display:'flex', 
					   flexDirection:'column', 
					   alignItems:'center', 
					   justifyContent:'center', 
					   gap:3, 
					   minWidth:0, 
					   flex:'1 1 auto',
                       width: '100%' }} >
        <div style={{ width:7, height:7, borderRadius:'50%', background:state.led, animation:state===PROBING_STATE?'hls-blink 1s ease-in-out infinite':'none', boxShadow:state===EMPTY?'none':`0 0 6px ${state.led}` }} />
        <span style={{ fontSize:11, fontWeight:700, color:'#fff', textAlign:'center', lineHeight:1.2, padding:'0 2px', overflow:'hidden', display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical' as React.CSSProperties['WebkitBoxOrient'], wordBreak:'break-word', opacity:disabled?0.5:1 }}>
          {cleanLabel(label) || '—'}
        </span>
        {state === PROBING_STATE && (
          <span style={{ display:'flex', gap:2, justifyContent:'center' }}>
            {[0,1,2].map(i => <span key={i} style={{ width:3, height:3, borderRadius:'50%', background:'#94a3b8', display:'inline-block', animation:`probe-fade 1.2s ease-in-out ${i*0.3}s infinite` }} />)}
          </span>
        )}
        {keyBinding && <span style={{ fontSize:9, fontFamily:'monospace', color:'#fff', lineHeight:1 }}>[{keyBinding}]</span>}
      </button>

      {onRetry && !isPoweredOff && (
        <div style={{ display:'flex', justifyContent:'center', paddingBottom:2 }}>
          <button onClick={e => { e.stopPropagation(); onRetry(); }} title="Réessayer"
            style={{ background:'rgba(249,115,22,0.15)', border:'1px solid rgba(249,115,22,0.5)', borderRadius:4, cursor:'pointer', padding:'2px 6px', display:'flex', alignItems:'center', gap:3, color:'#f97316' }}
            onMouseEnter={e=>(e.currentTarget.style.background='rgba(249,115,22,0.3)')}
            onMouseLeave={e=>(e.currentTarget.style.background='rgba(249,115,22,0.15)')}>
            <RefreshCw style={{ width:10, height:10 }} />
            <span style={{ fontSize:8, fontWeight:700 }}>Réessayer</span>
          </button>
        </div>
      )}

      {isActive && (
        <div style={{
          position     : 'absolute',
          inset        : 0,
          border       : `${ACTIVE_FRAME_WIDTH}px solid ${state.border}`,
          borderRadius : 8,
          pointerEvents: 'none',
          zIndex       : 1,
        }} />
      )}

      {hasAudio ? (
        <div style={{ padding:`3px ${ACTIVE_FRAME_WIDTH + 2}px ${ACTIVE_FRAME_WIDTH}px`, marginTop:'auto', display:'flex', flexDirection:'column', gap:7 }}>
          {/* Plus de slider de volume ici : le réglage se fait dans la fenêtre de configuration
              de la source (voies L/R). La hauteur du bouton est fixée par `height` sur le
              conteneur et le bouton principal est en flex:1 1 auto — il absorbe la place libérée. */}
          <SlotVuMeter sourceId={sourceId} />
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
            <button onClick={e => { e.stopPropagation(); onMuteToggle(); }} title={audioCfg.muted?'Unmute':'Mute'}
              style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', justifyContent:'center', color:audioCfg.muted?'#ef4444':'rgba(255,255,255,0.45)', width:20, height:20 }}>
              {audioCfg.muted ? <VolumeX style={{ width:14, height:14 }} /> : <Volume2 style={{ width:14, height:14 }} />}
            </button>
            {powerBtn}
            {configBtn}
          </div>
        </div>
      ) : (
        <div style={{ padding:`3px ${ACTIVE_FRAME_WIDTH + 2}px ${ACTIVE_FRAME_WIDTH}px`, marginTop:'auto', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
          {powerBtn}{configBtn}
        </div>
      )}
    </div>
  );
};

// ── T-Bar Fader ───────────────────────────────────────────────────────────────

interface TBarFaderProps {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
  height: number;
  /**
   * Valeur "live" à jour à CHAQUE frame (cf. useTbarTransition.barValueRef),
   * en plus de `value` (React state, throttlée côté hook). Quand fourni, le
   * curseur s'anime lui-même par écriture DOM directe dans une boucle
   * requestAnimationFrame — sans ça, un setState à 60fps dans un arbre aussi
   * gros que LiveStreamManager se fait absorber par React (rendu plus lent
   * que l'intervalle entre deux frames) et le curseur saute du départ à
   * l'arrivée sans image intermédiaire. `value` ne sert alors que de valeur
   * initiale au montage.
   */
  liveValueRef?: React.MutableRefObject<number>;
  /**
   * Point de repos courant (cf. useTbarTransition) : 0 = bas, 1 = haut. Ne
   * change qu'une fois par prise — sert à afficher un % qui reflète la
   * progression LOGIQUE de la transition en cours (0% au départ, 100% à
   * l'arrivée) plutôt que la position physique brute, qui elle ne bouge pas
   * toujours dans le même sens d'une prise à l'autre.
   */
  baseline?: 0 | 1;
}

const TBarFader: React.FC<TBarFaderProps> = ({ value, onChange, disabled, height, liveValueRef, baseline = 0 }) => {
  const trackRef   = useRef<HTMLDivElement>(null);
  const handleElRef = useRef<HTMLDivElement>(null);
  const pctElRef     = useRef<HTMLSpanElement>(null);
  const dragging   = useRef(false);

  const HANDLE_H   = 20;
  const HANDLE_OVF = 12;
  const TRACK_W    = 10;
  const TRACK_TOP  = 0;
  const FOOTER_H   = 0;
  const TRACK_H    = height - FOOTER_H;
  const USABLE     = TRACK_H - HANDLE_H;
  const TRACK_LEFT = HANDLE_OVF + 6;

  // Position physique → progression logique de la prise en cours, selon le
  // point de repos courant — même formule que useTbarTransition.toLogical.
  const toLogical = useCallback((physical: number) => (baseline === 0 ? physical : 1 - physical), [baseline]);

  const visualOf = useCallback((v: number) => {
    const logical  = toLogical(v);
    const isCenter = logical > 0.45 && logical < 0.55;
    return {
      top: (1 - v) * USABLE + TRACK_TOP,
      bg: isCenter
        ? 'linear-gradient(135deg, #475569, #64748b)'
        : logical > 0.5
          ? 'linear-gradient(135deg, #991b1b, #dc2626)'
          : 'linear-gradient(135deg, #166534, #22c55e)',
      border: isCenter ? '#94a3b8' : logical > 0.5 ? '#fca5a5' : '#86efac',
      pct: `${Math.round(logical * 100)}%`,
    };
  }, [USABLE, TRACK_TOP, toLogical]);

  // Boucle d'animation imperative : lit liveValueRef à chaque frame et écrit
  // le DOM directement, en court-circuitant le rendu React de tout l'arbre
  // parent. `value` (prop React) reste la source de vérité pour le premier
  // rendu (avant que la boucle démarre) et pour l'accessibilité/back-compat
  // si `liveValueRef` n'est pas fourni.
  useEffect(() => {
    if (!liveValueRef) return;
    let rafId: number;
    const tick = () => {
      const v = liveValueRef.current;
      const { top, bg, border, pct } = visualOf(v);
      const handleEl = handleElRef.current;
      if (handleEl) {
        handleEl.style.top = `${top}px`;
        handleEl.style.background = bg;
        handleEl.style.borderColor = border;
      }
      if (pctElRef.current) pctElRef.current.textContent = pct;
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [liveValueRef, visualOf]);

  const handleY = (1 - value) * USABLE;

  const startDrag = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    e.preventDefault();
    dragging.current = true;
    const updateFromEvent = (clientY: number) => {
      const rect = trackRef.current?.getBoundingClientRect();
      if (!rect) return;
      const y       = clientY - rect.top - HANDLE_H / 2;
      const clamped = Math.max(0, Math.min(USABLE, y));
      onChange(1 - clamped / USABLE);
    };
    const onMove = (ev: MouseEvent | TouchEvent) =>
      dragging.current && updateFromEvent('touches' in ev ? ev.touches[0].clientY : ev.clientY);
    const onUp = () => {
      dragging.current = false;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove as EventListener);
      window.removeEventListener('touchend', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove as EventListener, { passive: false });
    window.addEventListener('touchend', onUp);
    updateFromEvent('touches' in e
      ? (e as React.TouchEvent).touches[0].clientY
      : (e as React.MouseEvent).clientY);
  }, [disabled, onChange, USABLE]);

  const logicalValue = toLogical(value);
  const isCenter      = logicalValue > 0.45 && logicalValue < 0.55;
  const handleBg      = isCenter
    ? 'linear-gradient(135deg, #475569, #64748b)'
    : logicalValue > 0.5
      ? 'linear-gradient(135deg, #991b1b, #dc2626)'
      : 'linear-gradient(135deg, #166534, #22c55e)';
  const handleBorder = isCenter ? '#94a3b8' : logicalValue > 0.5 ? '#fca5a5' : '#86efac';

  // f = fraction verticale (0 = haut, 1 = bas). Repos en bas (baseline=0) :
  // ordre historique, 100% en haut. Repos en haut (baseline=1) : la piste
  // s'inverse, 0% en haut — la prochaine prise part de là et descend vers 100%.
  const logicalPctAt = (f: number) => (baseline === 0 ? 1 - f : f);
  const marks = [0.00, 0.25, 0.50, 0.75, 1.00].map(f => ({
    pct: f,
    label: `${Math.round(logicalPctAt(f) * 100)}%`,
    major: f === 0 || f === 0.5 || f === 1,
  }));

  return (
    <div style={{
      position: 'relative',
      width: '100%',
      height: '100%',
      userSelect: 'none',
    }}>

      {/* ── Track ── */}
      <div
        ref={trackRef}
        onMouseDown={startDrag}
        onTouchStart={startDrag}
        style={{
          position: 'absolute',
          left: TRACK_LEFT,
          top: TRACK_TOP,
          width: TRACK_W,
          height: TRACK_H,
          background: 'linear-gradient(180deg, #ef444420, #1e293b 50%, #22c55e20)',
          border: '1px solid rgba(255,255,255,0.1)',
          borderRadius: 4,
          cursor: disabled ? 'not-allowed' : 'ns-resize',
        }}
      />

      {/* ── Graduations ── */}
      {marks.map(({ pct, major }) => (
        <div key={pct} style={{
          position: 'absolute',
          left: TRACK_LEFT + TRACK_W,
          right: 10,
          top: pct * TRACK_H + TRACK_TOP - 0.5,
          height: 1,
          background: major ? 'rgba(255,255,255,0.20)' : 'rgba(255,255,255,0.08)',
          pointerEvents: 'none',
        }} />
      ))}

      {/* ── Handle ── */}
      <div ref={handleElRef} style={{
        position: 'absolute',
        left: TRACK_LEFT - HANDLE_OVF,
        top: handleY + TRACK_TOP,
        width: TRACK_W + HANDLE_OVF * 2,
        height: HANDLE_H,
        background: handleBg,
        border: `2px solid ${handleBorder}`,
        borderRadius: 4,
        cursor: disabled ? 'not-allowed' : 'grab',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 2px 6px rgba(0,0,0,0.5), inset 0 1px 2px rgba(255,255,255,0.12)',
        // Sans liveValueRef, on retombe sur l'ancien comportement (transition
        // CSS sur `top`, positions rares). Avec liveValueRef, la boucle rAF
        // écrit déjà une position par frame : une transition CSS par-dessus
        // ne ferait qu'ajouter un lissage/retard indésirable.
        transition: liveValueRef ? 'none' : (dragging.current ? 'none' : 'top 0.05s'),
        pointerEvents: 'none',
      }}>
        <span ref={pctElRef} style={{
          fontSize: 8,
          fontFamily: 'monospace',
          fontWeight: 700,
          color: 'rgba(255,255,255,0.75)',
          lineHeight: 1,
          textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          userSelect: 'none',
        }}>
          {Math.round(value * 100)}%
        </span>
      </div>

      {/* ── Labels ticks ── */}
      {marks.map(({ pct, label, major }) => (
        <span key={label} style={{
          position: 'absolute',
          right: 6,
          top: pct * TRACK_H + TRACK_TOP,
          transform: pct === 0.00 ? 'translateY(0%)' : pct === 1.00 ? 'translateY(-100%)' : 'translateY(-50%)',
          fontSize: major ? 9 : 8,
          fontFamily: 'monospace',
          color: major ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.28)',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          textAlign: 'right',
        }}>
          {label}
        </span>
      ))}

    </div>
  );
};
// ── Props ─────────────────────────────────────────────────────────────────────
export interface DeckProps {
  deckSlots: (VideoSource | null)[];
  selectedVideoSource: string;
  deckSlotLayouts: (LayoutSlot | null)[];
  onSlotLayoutChange: (slotIndex: number, layout: LayoutSlot | null) => void;
  onDeckPreviewLayout: (layout: LayoutSlot) => void;
  activeLayoutPreviewId: string | null;
  /** Faux = régie en observation : seuls les boutons Preview restent cliquables. */
  isHolder?: boolean;
  onDeckSwitch: (source: VideoSource, forceNewScreen?: boolean) => void;
  onDeckPreview: (source: VideoSource) => void;
  deckPreviewSourceId: string | null;
  onDeckCut: () => void;
  channelPublicRef: string | null;   // null = aucune chaîne identifiée
  serverUrl: string;
  channelActive: boolean;
  imageSlots: ImageSlot[];
  activeImageIds: Set<string>;
  onToggleImage: (slotId: string) => void;
  onImageChange?: (slots: ImageSlot[]) => void;
  soundSlots: SoundSlot[];
  activeSoundIds: Set<string>;
  onTriggerSound: (slot: SoundSlot) => void;
  onSoundChange?: (slots: SoundSlot[]) => void;
  jingleSlots: JingleSlot[];
  activeJingleId: string | null;
  onTriggerJingle: (slot: JingleSlot) => void;
  onJingleChange?: (slots: JingleSlot[]) => void;
  videoOverlaySlots: VideoOverlaySlot[];
  activeVideoOverlayIds: Set<string>;
  pipVideoEls: React.MutableRefObject<Map<string, { el: HTMLVideoElement; stream: MediaStream }>>;
  onStartVideoOverlay: (slot: VideoOverlaySlot) => void;
  keyMap: DeckKeyMap;
  onOpenKeyboardShortcuts?: () => void;
  onKeyMapChange: (km: DeckKeyMap) => void;
  /** Remet toute la régie à zéro — proposé dans le pupitre. */
  onResetRegie?: () => void;
  onOpenMediaboardConfig: (tab?: ConfigTab, layoutIdx?: number) => void;
  onOpenSourceAliasConfig: () => void;
  sourceAliases: SourceAliasMap;
  onOpenDeckFullscreen: () => void;
  layoutSlots: LayoutSlot[];
  activeLayoutId: string | null;
  onToggleLayout: (slot: LayoutSlot) => void;
  onLayoutChange?: (slots: LayoutSlot[]) => void;
  // v5.0 — les niveaux audio ne descendent plus en props : les VU-mètres
  // s'abonnent à `audioLevelStore` par sourceId (cf. VuMeter.tsx).
  getSourceAudioConfig: (sourceId: string) => SourceAudioConfig;
  onSourceVolumeChange: (sourceId: string, volume: number) => void;
  onSourceMuteToggle: (sourceId: string) => void;
  availableSources?: VideoSource[];
  /** Flux réels des sources caméra/écran/distant — cf. LiveStreamManager. Les sources HLS
   * ('url') n'y figurent jamais : elles sont décodées en direct par hls.js, pas par un
   * MediaStream. Uniquement utilisé pour la préview de SourceConfigModal. */
  previewStreams?: Map<string, MediaStream>;
  onSlotSourceChange: (slotIndex: number, newSource: VideoSource | null) => void;
  onDeckSwitchSource?: (source: VideoSource) => void;
  onAliasChange?: (sourceId: string, alias: string) => void;
  onAliasesChange?: (aliases: SourceAliasMap) => void;
  availableAudioDevices?: { deviceId: string; label: string; kind?: string }[];
  selectedAudioDeviceIds?: Map<string, string>;
  onAudioDeviceChange?: (sourceId: string, audioDeviceId: string) => void;
  stereoVolumes?: Map<string, StereoVolume>;
  onStereoVolumeChange?: (sourceId: string, stereo: StereoVolume) => void;
  audioEffectsMap?: Map<string, AudioEffectState>;
  onToggleAudioEffect?: (sourceId: string, effect: AudioEffectId) => void;
  audioEffectConfigsMap?: Map<string, Record<string, unknown>>;
  onAudioEffectConfigsChange?: (sourceId: string, configs: Record<string, unknown>) => void;
  videoEffectsSettingsMap?: Map<string, VideoEffectsSettings>;
  onVideoEffectsSettingsChange?: (sourceId: string, settings: VideoEffectsSettings) => void;
  onAudioVisualChange?: (sourceId: string, visual: AudioOnlyVisual) => void;
  tbarValue?: number;
  /** Valeur "live" à jour à chaque frame — cf. TBarFaderProps.liveValueRef. */
  tbarValueRef?: React.MutableRefObject<number>;
  /** Point de repos courant — cf. TBarFaderProps.baseline. */
  tbarBaseline?: 0 | 1;
  onTbarChange?: (v: number) => void;
  onTbarAuto?: () => void;
  tbarAutoActive?: boolean;
  tbarSpeed?: number;
  onTbarSpeedChange?: (v: number) => void;
  /** Enchaînement actif (cf. enchainements/registry.ts). */
  tbarEnchainementId?: string;
  onTbarEnchainementChange?: (id: string) => void;
  tbarEnchainementConfig?: unknown;
  onTbarEnchainementConfigChange?: (config: unknown) => void;
  onSourceConfigOpen?:  (sourceId: string, source: VideoSource | null) => Promise<void>;
  onSourceConfigClose?: (sourceId: string | null) => void;
  hlsProbeStatusMap: Map<string, HlsProbeStatus>;
  /** La source a-t-elle déjà répondu dans cette session ? Distingue une première connexion d'une reconnexion. */
  hlsWasEverOk: (sourceId: string) => boolean;
  onRetryHls: (sourceId: string, url: string) => void;
  /**
   * Réacquisition forcée d'une source, depuis la fenêtre de réglage d'un slot.
   * Contrairement à `onRetryHls`, qui relance une sonde de manifeste, celles-ci
   * jettent l'acquisition en cours et la refont — le recours quand une source est
   * bien présente dans le deck mais que sa mini-vue reste noire, ou que l'entrée
   * audio branchée n'est pas celle qui est associée à la vidéo.
   */
  onReloadSource?: (sourceId: string) => Promise<void>;
  onReloadAudio?:  (sourceId: string) => Promise<void>;
  poweredOffSlots: Set<string>;
  onSlotPowerToggle: (sourceId: string, currentlyOff: boolean) => void;
  isRegiePowered?: boolean;
  /** Callback appelé au mount pour exposer l'ouverture du modal pupitre au parent */
  onRegisterPupitreOpen?: (fn: () => void) => void;
  onAddHlsSource?: (url: string, xtream?: XtreamCredentials) => void;
  /**
   * Échange de deux emplacements de source, demandé par un glisser-déposer.
   *
   * Sources vidéo (ligne 1), rangée « Preview source » (ligne 2) et mini-vues du
   * multiview sont TROIS VUES DU MÊME TABLEAU d'emplacements : un unique point
   * d'entrée les réordonne toutes, chez le parent. C'est aussi ce qui empêche la
   * boucle — il n'y a pas trois écritures qui se répondent, il y en a une.
   */
  onSlotsReorder?: (fromIndex: number, toIndex: number) => void;
  /** Registre audio : VU-mètres du bloc Mixeur (cf. DeckMixerPanel). */
  audioSourceRegistry?: AudioSourceRegistry;
}

// ── buildVideoRow ─────────────────────────────────────────────────────────────
export interface VideoRow { slot: VideoSource | null; badge: string; isHlsVideo: boolean; isHlsLive: boolean; }
// channelPublicRef peut être null : sans chaîne identifiée, aucune ligne ne peut être
// reconnue comme l'antenne de la chaîne — et surtout, on ne veut pas qu'un
// `video_null` aille par hasard correspondre à quoi que ce soit.
export const buildVideoRow = (deckSlots: (VideoSource | null)[], channelPublicRef: string | null): VideoRow[] => {
  const rows: VideoRow[] = [];
  for (let i = 0; i < SLOT_COUNT; i++) {
    const slot = i < deckSlots.length ? deckSlots[i] : null;
    const isHlsVideo = !!(channelPublicRef && slot?.type === 'url' && slot.externalUrl?.includes(`video_${channelPublicRef}`));
    const isHlsLive  = !!(channelPublicRef && slot?.type === 'url' && slot.externalUrl?.includes(`live_${channelPublicRef}`));
    rows.push({ slot: slot ?? null, badge: String(i + 1), isHlsVideo, isHlsLive });
  }
  return rows;
};

// ── Styles partagés modales ────────────────────────────────────────────────────
const modalSelectStyle: React.CSSProperties = {
  width:'100%', padding:'8px 12px', borderRadius:8,
  background:'#0f172a', border:'1px solid #334155',
  color:'#e2e8f0', fontSize:13, outline:'none',
  cursor:'pointer', appearance:'auto', colorScheme:'dark',
  WebkitTextFillColor:'#e2e8f0',
};
const ModalSection: React.FC<{ label: string; icon: React.ReactNode; children: React.ReactNode }> = ({ label, icon, children }) => (
  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>{icon}<span style={{ fontSize:12, fontWeight:600, color:'#cbd5e1' }}>{label}</span></div>
    {children}
  </div>
);
const ModalKeyRow: React.FC<{
  label: string; accentColor: string; binding: KeyBinding | null;
  isCapturing: boolean; onStartCapture: () => void; onCancelCapture: () => void; onClear: () => void;
}> = ({ label, accentColor, binding, isCapturing, onStartCapture, onCancelCapture, onClear }) => (
  <div style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', borderRadius:8, background:isCapturing?'rgba(99,102,241,0.1)':'rgba(255,255,255,0.03)', border:`1px solid ${isCapturing?'rgba(99,102,241,0.5)':'rgba(255,255,255,0.07)'}`, transition:'all 0.15s' }}>
    <span style={{ width:8, height:8, borderRadius:'50%', background:accentColor, flexShrink:0, boxShadow:`0 0 5px ${accentColor}80` }} />
    <span style={{ fontSize:12, fontWeight:600, color:'#cbd5e1', flex:1 }}>{label}</span>
    {isCapturing
      ? <span style={{ fontSize:11, fontWeight:700, color:'#a5b4fc', fontFamily:'monospace', animation:'kbPulse 0.8s ease-in-out infinite' }}>Appuyez sur une touche…</span>
      : <span style={{ fontSize:11, fontWeight:700, fontFamily:'monospace', color:binding?'#e2e8f0':'rgba(148,163,184,0.35)', background:binding?'rgba(255,255,255,0.08)':'transparent', border:binding?'1px solid rgba(255,255,255,0.15)':'1px solid transparent', borderRadius:5, padding:binding?'2px 8px':'2px 0', minWidth:32, textAlign:'center' as React.CSSProperties['textAlign'] }}>{formatKeyBinding(binding)}</span>
    }
    {isCapturing
      ? <button onClick={onCancelCapture} style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:5, color:'#94a3b8', fontSize:10, fontWeight:700, padding:'3px 10px', cursor:'pointer' }}>Annuler</button>
      : <button onClick={onStartCapture}  style={{ background:'rgba(99,102,241,0.12)', border:'1px solid rgba(99,102,241,0.35)', borderRadius:5, color:'#a5b4fc', fontSize:10, fontWeight:700, padding:'3px 10px', cursor:'pointer' }}>Capturer</button>
    }
    {!isCapturing && (
      <button onClick={onClear} title="Effacer" style={{ background:'none', border:'1px solid rgba(255,255,255,0.1)', borderRadius:5, color:'rgba(148,163,184,0.5)', fontSize:10, fontWeight:700, padding:'3px 8px', cursor:'pointer' }}>×</button>
    )}
  </div>
);

// ── MediaSlotConfigModal ───────────────────────────────────────────────────────
// Modale de config pour un bouton Média (image / son / jingle)
// Choisir le slot média à affecter + raccourci clavier.
// Avertissement si le slot est déjà affecté ailleurs (comme les sources vidéo).

interface UnifiedMediaConfigModalProps {
  btnIndex:          number;
  currentAssignment: MediaAssignment | null;
  mediaAssignments:  (MediaAssignment | null)[]; 
  imageSlots:        ImageSlot[];
  soundSlots:        SoundSlot[];
  jingleSlots:       JingleSlot[];
  keyMap:            DeckKeyMap;
  onKeyMapChange:    (km: DeckKeyMap) => void;
  onAssign:          (a: MediaAssignment | null) => void;
  onClose:           () => void;
}

const KIND_COLORS: Record<MediaKind, string> = { image:'#22d3ee', sound:'#facc15', jingle:'#f87171' };

const UnifiedMediaConfigModal: React.FC<UnifiedMediaConfigModalProps> = ({
  btnIndex, currentAssignment,mediaAssignments,
  imageSlots, soundSlots, jingleSlots,
  keyMap, onKeyMapChange, onAssign, onClose,
}) => {
  const initVal = currentAssignment
    ? `${currentAssignment.kind}:${currentAssignment.slotId}`
    : '';
  const [selected,    setSelected]    = useState(initVal);
  const [localKeyMap, setLocalKeyMap] = useState<DeckKeyMap>(keyMap);
  const [capturing,   setCapturing]   = useState(false);
  const [kbConflict,  setKbConflict]  = useState<string | null>(null);
  const [pending,     setPending]     = useState<{ kind: MediaKind; slotId: string; otherBtnIdx: number } | null>(null);


  // Tous les médias configurés, toutes catégories confondues
  const allConfigured = [
    ...imageSlots.filter(s => s.url).map(s => ({ kind: 'image'  as MediaKind, slot: s as any })),
    ...soundSlots.filter(s => s.url).map(s => ({ kind: 'sound'  as MediaKind, slot: s as any })),
    ...jingleSlots.filter((s: any) => s.url).map(s => ({ kind: 'jingle' as MediaKind, slot: s as any })),
  ];

  const selectedKind: MediaKind | null = selected ? (selected.split(':')[0] as MediaKind) : null;
  const accentColor = selectedKind ? KIND_COLORS[selectedKind] : '#94a3b8';

  // ── Keymap : le raccourci appartient au média choisi (keyMap.image[i] →
  // imageSlots[i]…), comme le déclenche useLiveStreamSetup — pas au bouton.
  const kbTarget = ((): { row: MediaKind; idx: number } | null => {
    if (!selectedKind) return null;
    const id  = selected.split(':')[1];
    const arr = selectedKind === 'image' ? imageSlots : selectedKind === 'sound' ? soundSlots : jingleSlots;
    const idx = (arr as any[]).findIndex(s => s.id === id);
    return idx >= 0 ? { row: selectedKind, idx } : null;
  })();

  const getKb = (): KeyBinding | null =>
    kbTarget ? (localKeyMap[kbTarget.row]?.[kbTarget.idx] ?? null) : null;

  const setKb = (kb: KeyBinding | null) => {
    if (!kbTarget) return;
    const { row, idx } = kbTarget;
    setLocalKeyMap(prev => {
      const next = [...(prev[row] ?? [])];
      while (next.length <= idx) next.push(null);
      next[idx] = kb;
      return { ...prev, [row]: next };
    });
  };

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    if (!capturing) return;
    const h = (e: KeyboardEvent) => {
      e.preventDefault(); e.stopPropagation();
      if (['Control','Alt','Shift','Meta'].includes(e.key)) return;
      const nb: KeyBinding = { code: e.code, ctrl: e.ctrlKey, shift: e.shiftKey, alt: e.altKey, display: e.key };
      if (!kbTarget) { setCapturing(false); return; }
      const c = findConflict(localKeyMap, nb, kbTarget.row, kbTarget.idx);
      setKb(nb);
      setKbConflict(c ? `Conflit avec « ${c.row} » slot ${c.idx + 1}` : null);
      setCapturing(false);
    };
    window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  // kbTarget / setKb sont recalculés à chaque rendu à partir de `selected`
  }, [capturing, localKeyMap, btnIndex, selected]); // eslint-disable-line react-hooks/exhaustive-deps

const handleConfirm = () => {
  const assignment: MediaAssignment | null = selected
    ? { kind: selected.split(':')[0] as MediaKind, slotId: selected.split(':')[1] }
    : null;
  onAssign(assignment);
  onKeyMapChange(localKeyMap);
  onClose();
};

  return ReactDOM.createPortal(
    <div
      style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background:'linear-gradient(180deg,#1e293b,#0f172a)', border:'1px solid #334155', borderRadius:16, width:'100%', maxWidth:420, boxShadow:'0 24px 48px rgba(0,0,0,0.5)', overflow:'hidden', display:'flex', flexDirection:'column' }}>

        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', borderBottom:'1px solid #334155', flexShrink:0 }}>
          <Settings style={{ width:16, height:16, color:accentColor }} />
          <span style={{ fontSize:14, fontWeight:700, color:'#fff', flex:1 }}>Média — bouton {btnIndex + 1}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', display:'flex', padding:4, borderRadius:6 }}>
            <X style={{ width:16, height:16 }} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding:18, display:'flex', flexDirection:'column', gap:14, overflowY:'auto' }}>
          <style>{`@keyframes kbPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

          <ModalSection label="Média affecté" icon={<Music style={{ width:13, height:13, color:accentColor }} />}>
            <select value={selected} onChange={e => {
  const val = e.target.value;
  setSelected(val);
  if (!val) { setPending(null); return; }
  const [k, id] = val.split(':');
  // Chercher si ce slot est déjà affecté sur un autre bouton
  const otherIdx = mediaAssignments.findIndex(
    (a, i) => i !== btnIndex && a?.kind === k && a?.slotId === id
  );
  if (otherIdx >= 0) {
    setPending({ kind: k as MediaKind, slotId: id, otherBtnIdx: otherIdx });
  } else {
    setPending(null);
  }
}} style={modalSelectStyle}>
              <option value="" style={{ background:'#1a2035', color:'#e2e8f0' }}>— Aucun —</option>

              {(['image','sound','jingle'] as MediaKind[]).map(k => {
                const items = allConfigured.filter(m => m.kind === k);
                if (!items.length) return null;
                const groupLabel = k === 'image' ? '🖼 Images' : k === 'sound' ? '🎵 Sons' : '🎬 Vidéos';
                return (
                  <optgroup key={k} label={groupLabel}>
                    {items.map(({ kind, slot }) => (
                      <option key={slot.id} value={`${kind}:${slot.id}`} style={{ background:'#1a2035', color:'#e2e8f0' }}>
                        {cleanLabel(slot.label) || slot.id}
                      </option>
                    ))}
                  </optgroup>
                );
              })}

              {allConfigured.length === 0 && (
                <option disabled style={{ background:'#1a2035', color:'#64748b' }}>
                  Aucun média configuré
                </option>
              )}
            </select>
			{pending && (
  <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'10px 12px', background:'rgba(234,88,12,0.15)', border:'1px solid rgba(234,88,12,0.4)', borderRadius:10, flexDirection:'column' }}>
    <div style={{ display:'flex', alignItems:'flex-start', gap:8 }}>
      <AlertTriangle style={{ width:14, height:14, color:'#f97316', flexShrink:0, marginTop:1 }} />
      <span style={{ fontSize:12, fontWeight:600, color:'#fdba74', lineHeight:1.4 }}>
        Ce média est déjà affecté au bouton {pending.otherBtnIdx + 1}. Il sera retiré de ce bouton et placé ici.
      </span>
    </div>
    <div style={{ display:'flex', gap:8, justifyContent:'flex-end', width:'100%' }}>
      <button
        onClick={() => { setSelected(initVal); setPending(null); }}
        style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:6, color:'#94a3b8', fontSize:11, fontWeight:600, padding:'5px 14px', cursor:'pointer' }}
      >Annuler</button>
      <button
        onClick={() => setPending(null)}
        style={{ background:'linear-gradient(135deg,#9a3412,#ea580c)', border:'1px solid #f97316', borderRadius:6, color:'#fff', fontSize:11, fontWeight:700, padding:'5px 14px', cursor:'pointer' }}
      >Confirmer</button>
    </div>
  </div>
)}
          </ModalSection>

          <ModalSection label="Raccourci clavier" icon={<Keyboard style={{ width:13, height:13, color:'#f472b6' }} />}>
            {kbConflict && (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', background:'rgba(234,88,12,0.12)', border:'1px solid rgba(234,88,12,0.35)', borderRadius:8 }}>
                <AlertTriangle style={{ width:12, height:12, color:'#f97316', flexShrink:0 }} />
                <span style={{ fontSize:11, color:'#fdba74' }}>{kbConflict}</span>
              </div>
            )}
            <ModalKeyRow
              label="Média"
              accentColor={accentColor}
              binding={getKb()}
              isCapturing={capturing}
              onStartCapture={() => { setCapturing(true); setKbConflict(null); }}
              onCancelCapture={() => setCapturing(false)}
              onClear={() => setKb(null)}
            />
          </ModalSection>
        </div>

        {/* Footer */}
        <div style={{ padding:'10px 18px', borderTop:'1px solid rgba(255,255,255,0.08)', display:'flex', justifyContent:'flex-end', gap:8, flexShrink:0 }}>
          <button onClick={onClose}       style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:6, color:'#94a3b8', fontSize:12, fontWeight:700, padding:'6px 18px', cursor:'pointer' }}>Annuler</button>
          <button onClick={handleConfirm} style={{ background:'rgba(99,102,241,0.15)', border:'1px solid rgba(99,102,241,0.4)',   borderRadius:6, color:'#a5b4fc', fontSize:12, fontWeight:700, padding:'6px 18px', cursor:'pointer' }}>Valider</button>
        </div>
      </div>
    </div>,
    document.body
  );
};


// ── SceneSlotConfigModal ───────────────────────────────────────────────────────
// Modale de config pour un bouton Scène : choisir la scène + raccourci clavier

interface SceneSlotConfigModalProps {
  slotIndex     : number;
  currentSlot   : LayoutSlot | null; // scène actuellement affectée à ce bouton deck
  layoutSlots   : LayoutSlot[];      // toutes les scènes disponibles
  keyMap        : DeckKeyMap;
  onKeyMapChange: (km: DeckKeyMap) => void;
  onSlotAssign  : (slotIndex: number, layout: LayoutSlot | null) => void;
  onClose       : () => void;
}

const SceneSlotConfigModal: React.FC<SceneSlotConfigModalProps> = ({
  slotIndex, currentSlot, layoutSlots, keyMap, onKeyMapChange, onSlotAssign, onClose,
}) => {
  const [selected,    setSelected]    = useState<string>(currentSlot?.id ?? '');
  const [localKeyMap, setLocalKeyMap] = useState<DeckKeyMap>(keyMap);
  const [capturing,   setCapturing]   = useState(false);
  const [conflict,    setConflict]    = useState<string | null>(null);


  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  useEffect(() => {
    if (!capturing) return;
    const h = (e: KeyboardEvent) => {
      e.preventDefault(); e.stopPropagation();
      if (['Control','Alt','Shift','Meta'].includes(e.key)) return;
      const nb: KeyBinding = { code:e.code, ctrl:e.ctrlKey, shift:e.shiftKey, alt:e.altKey, display:e.key };
      const c = findConflict(localKeyMap, nb, 'layout', slotIndex);
      setLocalKeyMap(prev => ({ ...prev, layout: prev.layout.map((kb: KeyBinding|null,i: number) => i === slotIndex ? nb : kb) }));
      setConflict(c ? `Conflit avec « ${c.row} » slot ${c.idx+1}` : null);
      setCapturing(false);
    };
    window.addEventListener('keydown', h, { capture: true });
    return () => window.removeEventListener('keydown', h, { capture: true });
  }, [capturing, localKeyMap, slotIndex]);

  const handleConfirm = () => {
    const layout = layoutSlots.find(l => l.id === selected) ?? null;
    onSlotAssign(slotIndex, layout);
    onKeyMapChange(localKeyMap);
    onClose();
  };

  const availableScenes = layoutSlots.filter(l => l.background.type !== 'none' || l.incrustations.length > 0);

  return ReactDOM.createPortal(
    <div
      style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'linear-gradient(180deg,#1e293b,#0f172a)', border:'1px solid #334155', borderRadius:16, width:'100%', maxWidth:420, boxShadow:'0 24px 48px rgba(0,0,0,0.5)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', borderBottom:'1px solid #334155', flexShrink:0 }}>
          <Clapperboard style={{ width:16, height:16, color:'#60a5fa' }} />
          <span style={{ fontSize:14, fontWeight:700, color:'#fff', flex:1 }}>Scène — bouton {slotIndex + 1}</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', display:'flex', padding:4, borderRadius:6 }}><X style={{ width:16, height:16 }} /></button>
        </div>
        {/* Body */}
        <div style={{ padding:'18px', display:'flex', flexDirection:'column', gap:16 }}>
          <style>{`@keyframes kbPulse { 0%,100%{opacity:1} 50%{opacity:0.4} }`}</style>

          <ModalSection label="Scène affectée" icon={<Clapperboard style={{ width:13, height:13, color:'#60a5fa' }} />}>
            <select value={selected} onChange={e => setSelected(e.target.value)} style={modalSelectStyle} className="scm-select">
              <option value="" style={{ background:'#1a2035', color:'#e2e8f0' }}>— Aucune scène —</option>
              {availableScenes.map(l => (
                <option key={l.id} value={l.id} style={{ background:'#1a2035', color:'#e2e8f0' }}>
                  {cleanLabel(l.label) || l.id}
                </option>
              ))}
            </select>
          </ModalSection>

          <ModalSection label="Raccourci clavier" icon={<Keyboard style={{ width:13, height:13, color:'#f472b6' }} />}>
            {conflict && (
              <div style={{ display:'flex', alignItems:'center', gap:8, padding:'7px 12px', background:'rgba(234,88,12,0.12)', border:'1px solid rgba(234,88,12,0.35)', borderRadius:8 }}>
                <AlertTriangle style={{ width:12, height:12, color:'#f97316', flexShrink:0 }} />
                <span style={{ fontSize:11, color:'#fdba74' }}>{conflict}</span>
              </div>
            )}
            <ModalKeyRow
              label="Scène"
              accentColor="#60a5fa"
              binding={localKeyMap.layout?.[slotIndex] ?? null}
              isCapturing={capturing}
              onStartCapture={() => { setCapturing(true); setConflict(null); }}
              onCancelCapture={() => setCapturing(false)}
              onClear={() => setLocalKeyMap(prev => ({ ...prev, layout: prev.layout.map((kb: KeyBinding|null,i: number) => i === slotIndex ? null : kb) }))}
            />
          </ModalSection>
        </div>
        {/* Footer */}
        <div style={{ padding:'10px 18px', borderTop:'1px solid rgba(255,255,255,0.08)', display:'flex', justifyContent:'flex-end', gap:8, flexShrink:0 }}>
          <button onClick={onClose}       style={{ background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.15)', borderRadius:6, color:'#94a3b8', fontSize:12, fontWeight:700, padding:'6px 18px', cursor:'pointer' }}>Annuler</button>
          <button onClick={handleConfirm} style={{ background:'rgba(99,102,241,0.15)', border:'1px solid rgba(99,102,241,0.4)',   borderRadius:6, color:'#a5b4fc', fontSize:12, fontWeight:700, padding:'6px 18px', cursor:'pointer' }}>Valider</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── MediaKindPickerModal ──────────────────────────────────────────────────────
interface MediaKindPickerModalProps {
  onPick: (kind: 'image' | 'sound' | 'jingle') => void;
  onClose: () => void;
}
const MEDIA_KIND_CHOICES: { kind: 'image' | 'sound' | 'jingle'; label: string; icon: React.ReactNode; color: string }[] = [
  { kind: 'image',  label: 'Image', icon: <Image style={{ width: 20, height: 20 }} />, color: '#22d3ee' },
  { kind: 'sound',  label: 'Son',   icon: <Music style={{ width: 20, height: 20 }} />, color: '#facc15' },
  { kind: 'jingle', label: 'Vidéo', icon: <Film  style={{ width: 20, height: 20 }} />, color: '#f87171' },
];
const MediaKindPickerModal: React.FC<MediaKindPickerModalProps> = ({ onPick, onClose }) => {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      onClick={onClose}
      style={{ position:'fixed', inset:0, zIndex:9999, background:'rgba(0,0,0,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', padding:24 }}>
      <div onClick={e => e.stopPropagation()} style={{ background:'linear-gradient(180deg,#1e293b,#0f172a)', border:'1px solid #334155', borderRadius:16, width:'100%', maxWidth:340, boxShadow:'0 24px 48px rgba(0,0,0,0.5)', overflow:'hidden', display:'flex', flexDirection:'column' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, padding:'14px 18px', borderBottom:'1px solid #334155', flexShrink:0 }}>
          <Plus style={{ width:16, height:16, color:'#93c5fd' }} />
          <span style={{ fontSize:14, fontWeight:700, color:'#fff', flex:1 }}>Ajouter un média</span>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', display:'flex', padding:4, borderRadius:6 }}><X style={{ width:16, height:16 }} /></button>
        </div>
        <div style={{ padding:18, display:'flex', gap:10 }}>
          {MEDIA_KIND_CHOICES.map(c => (
            <button key={c.kind} onClick={() => onPick(c.kind)} style={{
              flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6,
              padding:'16px 8px', borderRadius:10, cursor:'pointer',
              background:`${c.color}15`, border:`1px solid ${c.color}40`, color:c.color, fontSize:12, fontWeight:700,
            }}>
              {c.icon}
              {c.label}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── PupitreConfigModalWrapper ─────────────────────────────────────────────────
interface PupitreWrapperProps {
  onClose: () => void; videoRow: VideoRow[];
  deckSlots: (VideoSource | null)[]; deckSlotLayouts: (LayoutSlot | null)[];
  availableSources: VideoSource[]; sourceAliases: SourceAliasMap;
  onAliasesChange: ((aliases: SourceAliasMap) => void) | undefined;
  onSlotSourceChange: (slotIndex: number, newSource: VideoSource | null) => void;
  onSlotLayoutChange: (slotIndex: number, layout: LayoutSlot | null) => void;
  jingleSlots: JingleSlot[]; layoutSlots: LayoutSlot[];
  imageSlots: ImageSlot[]; soundSlots: SoundSlot[]; videoOverlaySlots: VideoOverlaySlot[];
  activeJingleId: string | null; activeLayoutId: string | null;
  onTriggerJingle: (slot: JingleSlot) => void; onToggleLayout: (slot: LayoutSlot) => void;
  keyMap: DeckKeyMap; onKeyMapChange: (km: DeckKeyMap) => void;
  onResetRegie?: () => void;
  slotLabelFn: (idx: number) => string | null;
}
const PupitreConfigModalWrapper: React.FC<PupitreWrapperProps> = (p) => {
  const vSlots: Array<VideoSource | null> = [];
  const vLayouts: Array<LayoutSlot | null> = [];
  for (let vi = 0; vi < p.videoRow.length; vi++) {
    const row = p.videoRow[vi];
    vSlots.push(row.slot ?? null);
    if (!row.slot) { vLayouts.push(null); } else {
      let ri = -1;
      for (let i = 0; i < p.deckSlots.length; i++) {
        if (p.deckSlots[i]?.id === row.slot.id) { ri = i; break; }
      }
      vLayouts.push(ri >= 0 ? (p.deckSlotLayouts[ri] ?? null) : null);
    }
  }
  const deckSrcIds: Set<string> = new Set();
  for (let vi = 0; vi < p.videoRow.length; vi++) if (p.videoRow[vi].slot) deckSrcIds.add(p.videoRow[vi].slot!.id);
  const oSources: VideoSource[] = [];
  for (let vi = 0; vi < p.videoRow.length; vi++) if (p.videoRow[vi].slot) oSources.push(p.videoRow[vi].slot!);
  for (let i = 0; i < p.availableSources.length; i++) if (!deckSrcIds.has(p.availableSources[i].id)) oSources.push(p.availableSources[i]);

  const usedRaw = new Set<number>();
  const mapping: number[] = p.videoRow.map(() => -1);
  for (let vi = 0; vi < p.videoRow.length; vi++) {
    if (p.videoRow[vi].slot) {
      for (let i = 0; i < p.deckSlots.length; i++) {
        if (p.deckSlots[i]?.id === p.videoRow[vi].slot!.id) { mapping[vi] = i; usedRaw.add(i); break; }
      }
    }
  }
  const freeRaw: number[] = [];
  for (let i = 0; i < p.deckSlots.length; i++) if (!usedRaw.has(i)) freeRaw.push(i);
  let fi = 0;
  for (let vi = 0; vi < p.videoRow.length; vi++) {
    if (!p.videoRow[vi].slot && mapping[vi] === -1 && fi < freeRaw.length) { mapping[vi] = freeRaw[fi]; fi++; }
  }

  const handleSrcChange    = (vi: number, s: VideoSource | null) => { const ri = mapping[vi]; if (ri >= 0) p.onSlotSourceChange(ri, s); };
  const handleLayoutChange = (vi: number, l: LayoutSlot | null) => { const ri = mapping[vi]; if (ri >= 0) p.onSlotLayoutChange(ri, l); };

  const vLabels  = p.videoRow.map((_, vi) => p.slotLabelFn(vi));
  const iLabels  = p.imageSlots.map(s => s.label ?? '');
  const sLabels  = p.soundSlots.map(s => s.label ?? '');
  const pLabels  = p.videoOverlaySlots.map(s => s.label ?? '');
  const jLabels  = p.jingleSlots.map(s => s.label ?? '');
  const lLabels  = p.layoutSlots.map(s => s.label ?? '');

  return (
    <PupitreConfigModal
      onClose={p.onClose} availableSources={oSources}
      deckSlots={vSlots} deckSlotLayouts={vLayouts}
      aliases={p.sourceAliases}
      onAliasesChange={aliases => p.onAliasesChange && p.onAliasesChange(aliases)}
      onSlotSourceChange={handleSrcChange} onSlotLayoutChange={handleLayoutChange}
      jingleSlots={p.jingleSlots} layoutSlots={p.layoutSlots}
      activeJingleId={p.activeJingleId} activeLayoutId={p.activeLayoutId}
      onTriggerJingle={p.onTriggerJingle} onToggleLayout={p.onToggleLayout}
      keyMap={p.keyMap} onKeyMapChange={p.onKeyMapChange} onResetRegie={p.onResetRegie}
      videoSlotLabels={vLabels} imageSlotLabels={iLabels} soundSlotLabels={sLabels}
      pipSlotLabels={pLabels} jingleSlotLabels={jLabels} layoutSlotLabels={lLabels}
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Composant principal V2.4
// ─────────────────────────────────────────────────────────────────────────────
const DeckV2: React.FC<DeckProps> = (props) => {
  const {
    deckSlots, selectedVideoSource, deckSlotLayouts, onSlotLayoutChange,
    onDeckPreviewLayout, activeLayoutPreviewId,
    isHolder = true,
    onDeckSwitch, onDeckPreview, deckPreviewSourceId, onDeckCut,
    channelPublicRef, serverUrl, imageSlots, activeImageIds, onToggleImage, soundSlots, activeSoundIds, onTriggerSound, jingleSlots, activeJingleId, onTriggerJingle, videoOverlaySlots, keyMap, onKeyMapChange, onOpenMediaboardConfig,
    sourceAliases,
    layoutSlots, activeLayoutId, onToggleLayout, onLayoutChange,
    getSourceAudioConfig, onSourceMuteToggle,
    onSlotSourceChange, onDeckSwitchSource, onAliasChange, onAliasesChange,
    availableSources, previewStreams, availableAudioDevices, selectedAudioDeviceIds, onAudioDeviceChange,
    stereoVolumes, onStereoVolumeChange, audioEffectsMap, onToggleAudioEffect,
    audioEffectConfigsMap, onAudioEffectConfigsChange,
    videoEffectsSettingsMap, onVideoEffectsSettingsChange, onAudioVisualChange,
    tbarValue = 0, tbarValueRef, tbarBaseline = 0, onTbarChange, onTbarAuto, tbarAutoActive = false,
    tbarSpeed = 1.0, onTbarSpeedChange,
    tbarEnchainementId = 'fondu', onTbarEnchainementChange,
    tbarEnchainementConfig, onTbarEnchainementConfigChange,
    onSourceConfigOpen, onSourceConfigClose,
    hlsProbeStatusMap, hlsWasEverOk, onRetryHls,
    onReloadSource, onReloadAudio,
    poweredOffSlots, onSlotPowerToggle,
    isRegiePowered = true,
    onRegisterPupitreOpen,
    onImageChange, onSoundChange, onJingleChange,
    onSlotsReorder,
    audioSourceRegistry,
  } = props;

  const [cutFlash,         setCutFlash        ] = useState(false);
  const [configSlotIndex,  setConfigSlotIndex ] = useState<number | null>(null);
  const [pupitreConfigOpen,setPupitreConfigOpen] = useState(false);
  const [tbarSettingsOpen, setTbarSettingsOpen] = useState(false);
  const [pendingPowerOff,  setPendingPowerOff ] = useState<{ sourceId: string; message: string } | null>(null);

  // Modales média/scène
  
const [mediaConfigBtnIdx, setMediaConfigBtnIdx] = useState<number | null>(null);

// ── Glisser-déposer : état visuel, une famille à la fois ─────────────────────
//
// Un seul état pour les trois familles : on ne peut glisser qu'un élément à la
// fois, et mémoriser la famille évite d'allumer la cible de dépôt d'une rangée
// pendant qu'on déplace dans une autre.
const [dnd, setDnd] = useState<{ family: ReorderFamily | null; from: number | null; over: number | null }>(
  { family: null, from: null, over: null }
);
const makeDragState = (family: ReorderFamily) => (dragged: number | null, over: number | null) => {
  setDnd(prev => {
    if (dragged === null && over === null) return { family: null, from: null, over: null };
    if (dragged !== null)                  return { family, from: dragged, over: null };
    return { ...prev, family, over };
  });
};
const isDraggedIn    = (family: ReorderFamily, idx: number) => dnd.family === family && dnd.from === idx;
const isDropTargetIn = (family: ReorderFamily, idx: number) => dnd.family === family && dnd.over === idx && dnd.from !== idx;

const [mediaAssignments, setMediaAssignments] = useState<(MediaAssignment | null)[]>(() => {
  // Remplissage dans l'ordre images → sons → jingles, jusqu'à épuisement des boutons
  const a: (MediaAssignment | null)[] = Array(TOTAL_MEDIA_BTNS).fill(null);
  let n = 0;
  const seed = (kind: MediaKind, arr: { id: string; url?: string | null }[]) => {
    for (const s of arr) {
      if (n >= TOTAL_MEDIA_BTNS) return;
      if (s.url) a[n++] = { kind, slotId: s.id };
    }
  };
  seed('image',  imageSlots);
  seed('sound',  soundSlots);
  seed('jingle', jingleSlots as any[]);
  return a;
});  
  
// Sync : ajoute les nouveaux slots configurés sur les boutons vides, retire les slots vidés
useEffect(() => {
  setMediaAssignments(prev => {
    const next = [...prev];
    const assignedIds = new Set(next.filter(Boolean).map(a => a!.slotId));

    const allSlots: [MediaKind, any[]][] = [
      ['image',  imageSlots],
      ['sound',  soundSlots],
      ['jingle', jingleSlots],
    ];

    // Retire les affectations dont le slot n'a plus d'URL
    for (let i = 0; i < next.length; i++) {
      if (!next[i]) continue;
      const { kind, slotId } = next[i]!;
      const arr = kind === 'image' ? imageSlots : kind === 'sound' ? soundSlots : jingleSlots;
      const slot = (arr as any[]).find((s: any) => s.id === slotId);
      if (!slot || !slot.url) { next[i] = null; assignedIds.delete(slotId); }
    }

    // Ajoute les nouveaux slots configurés dans le premier bouton vide global
    for (const [kind, arr] of allSlots) {
      for (const slot of arr as any[]) {
        if (!slot.url || assignedIds.has(slot.id)) continue;
        const firstFree = next.findIndex(a => a === null);
        if (firstFree === -1) break;
        next[firstFree] = { kind, slotId: slot.id };
        assignedIds.add(slot.id);
      }
    }

    return next;
  });
}, [imageSlots, soundSlots, jingleSlots]);
  
  
  const [sceneConfig,  setSceneConfig]  = useState<number | null>(null); // index bouton scène
  const [mediaAddPicker, setMediaAddPicker] = useState(false); // popup choix image/son/vidéo pour le "+"

  /**
   * Échange deux boutons média.
   *
   * Deux tableaux distincts, et c'est voulu : `mediaAssignments` dit QUEL slot
   * occupe QUEL bouton (il le désigne par son id), et `imageSlots` / `soundSlots`
   * / `jingleSlots` sont les bibliothèques que montre la Configuration du
   * Mediaboard. Échanger les affectations déplace les boutons ; échanger les
   * slots déplace les listes de la configuration. L'opérateur attend les deux.
   *
   * Familles différentes — une image lâchée sur un son — : seuls les boutons
   * s'échangent. Il n'existe aucun tableau commun où les échanger, et inventer
   * un ordre entre une image et un son n'aurait aucun sens dans la config.
   *
   * Pas de boucle possible : l'effet de synchronisation ne fait que remplir les
   * boutons vides et vider ceux dont le slot a perdu son URL. Deux boutons
   * pleins échangés sont un point fixe pour lui, et l'ordre des bibliothèques
   * n'entre pas dans le calcul des positions — les affectations désignent les
   * slots par id.
   */
  const handleMediaReorder = (from: number, to: number) => {
    const plan = planMediaReorder(mediaAssignments, from, to);
    if (plan.assignments === mediaAssignments) return;

    setMediaAssignments(plan.assignments);

    if (!plan.librarySwap) return;
    const { kind, fromId, toId } = plan.librarySwap;

    if (kind === 'image') {
      const next = applyLibrarySwap(imageSlots, fromId, toId);
      if (next !== imageSlots) onImageChange?.(next);
    } else if (kind === 'sound') {
      const next = applyLibrarySwap(soundSlots, fromId, toId);
      if (next !== soundSlots) onSoundChange?.(next);
    } else {
      const next = applyLibrarySwap(jingleSlots, fromId, toId);
      if (next !== jingleSlots) onJingleChange?.(next);
    }
  };

  /**
   * Échange deux scènes. `layoutSlots` est ici la seule source de vérité — les
   * boutons la lisent directement, et la Configuration du Mediaboard aussi :
   * il n'y a rien à synchroniser, donc rien qui puisse boucler.
   *
   * Les scènes posées sur un emplacement vidéo (`deckSlotLayouts`) ne bougent
   * pas : elles désignent une scène, pas une position dans cette liste.
   */
  const handleSceneReorder = (from: number, to: number) => {
    const next = swapAt(layoutSlots, from, to);
    if (next !== layoutSlots) onLayoutChange?.(next);
  };

  const handleMediaKindPick = (kind: 'image' | 'sound' | 'jingle') => {
    setMediaAddPicker(false);
    const tab: ConfigTab = kind === 'image' ? 'images' : kind === 'sound' ? 'sounds' : 'jingles';
    const idx = kind === 'image'
      ? imageSlots.findIndex(s => !s.url)
      : kind === 'sound'
        ? soundSlots.findIndex(s => !s.url)
        : jingleSlots.findIndex(s => !s.url);
    onOpenMediaboardConfig(tab, idx >= 0 ? idx : 0);
  };

  // Exposer l'ouverture du modal pupitre vers le parent
  useEffect(() => {
    onRegisterPupitreOpen?.(() => setPupitreConfigOpen(true));
  }, [onRegisterPupitreOpen]);

  // Hauteurs V2.5
  // BTN_H2         : boutons preview/scènes/médias (−2px vs V2.4)
  // BTN_H1_VIDEO   : boutons sources = BTN_H2*2 + MEDIA_GAP − 4 (−4px net)
  // BTN_TRANSITION : bouton Transition = BTN_H2/2 − 4
  
  const BTN_H2         =  69;
  const MEDIA_GAP      =   5;
  const BTN_H1_VIDEO   = BTN_H2 * 2 + MEDIA_GAP;  // 73*2+5-4 = 147
  const BTN_SQUARE     = Math.round(BTN_H2 / 2);
  const BTN_SMALL      = Math.round(BTN_H2 / 3);
  const BTN_TINY       = Math.round(BTN_SMALL * 0.65);
  // Rangée AUTO + engrenage sous le T-bar : plus besoin de place pour le champ
  // "Vit." (déplacé dans TbarSettingsModal) ni pour le bouton Transition à
  // part (fondu dans le même engrenage) — tout l'espace libéré va au T-bar.
  const BTN_TBAR_ROW   = 30;
  const BTN_TBAR_H     = BTN_H1_VIDEO - MEDIA_GAP - BTN_TBAR_ROW;

  // ── Helpers ────────────────────────────────────────────────────────────────

  const handleOpenSourceConfig = useCallback(async (slotIndex: number) => {
    setConfigSlotIndex(slotIndex);
    const layout   = deckSlotLayouts[slotIndex];
    const source   = deckSlots[slotIndex] ?? null;
    const sourceId = layout ? layout.id : (source ? source.id : `slot-${slotIndex}`);
    if (onSourceConfigOpen) await onSourceConfigOpen(sourceId, source);
  }, [deckSlots, deckSlotLayouts, onSourceConfigOpen]);

  const handleCloseSourceConfig = useCallback(() => {
    if (configSlotIndex !== null) {
      const layout   = deckSlotLayouts[configSlotIndex];
      const source   = deckSlots[configSlotIndex] ?? null;
      const sourceId = layout ? layout.id : (source ? source.id : null);
      if (onSourceConfigClose) onSourceConfigClose(sourceId);
    }
    setConfigSlotIndex(null);
  }, [configSlotIndex, deckSlots, deckSlotLayouts, onSourceConfigClose]);

  const hlsStatus = useCallback((isVideo: boolean): HlsProbeStatus | undefined => {
    const id = isVideo ? `hls-video-${channelPublicRef}` : `hls-live-${channelPublicRef}`;
    return hlsProbeStatusMap.get(id);
  }, [hlsProbeStatusMap, channelPublicRef]);

  const hlsUrl = useCallback((isVideo: boolean): string => {
    const base = serverUrl.replace(/\/$/, '');
    return isVideo ? `${base}/video_${channelPublicRef}/index.m3u8` : `${base}/live_${channelPublicRef}/index.m3u8`;
  }, [serverUrl, channelPublicRef]);

  const handleCut = () => { onDeckCut(); setCutFlash(true); setTimeout(() => setCutFlash(false), 300); };
  const videoRow  = buildVideoRow(deckSlots, channelPublicRef);

  const buildHls = (isV: boolean): VideoSource => {
    const base = serverUrl.replace(/\/$/, '');
    return isV
      ? { id:`hls-video-${channelPublicRef}`, label:'Playlist',     type:'url', externalUrl:`${base}/video_${channelPublicRef}/index.m3u8` }
      : { id:`hls-live-${channelPublicRef}`,  label:'OBS, RTMP...', type:'url', externalUrl:`${base}/live_${channelPublicRef}/index.m3u8` };
  };

  const isHlsActive = (isV: boolean) =>
    selectedVideoSource === (isV ? `hls-video-${channelPublicRef}` : `hls-live-${channelPublicRef}`);

  const srcIdForSlot = (idx: number): string => {
    const layout = deckSlotLayouts[idx];
    if (layout) return layout.id;
    const row = videoRow[idx];
    if (!row) return '';
    return row.slot ? row.slot.id : (row.isHlsVideo ? `hls-video-${channelPublicRef}` : row.isHlsLive ? `hls-live-${channelPublicRef}` : '');
  };

  const slotState = (idx: number): ColorState => {
    const srcId = srcIdForSlot(idx);
    if (poweredOffSlots.has(srcId)) return EMPTY;
    const layout = deckSlotLayouts[idx];
    if (layout) {
      const hasContent = layout.background.type !== 'none' || layout.incrustations.length > 0;
      return stateOfRow('video', hasContent, activeLayoutId === layout.id);
    }
    const row = videoRow[idx];
    if (!row) return EMPTY;
    if (row.isHlsVideo || row.isHlsLive) {
      const s = hlsStatus(row.isHlsVideo);
      if (s === 'failed')  return FAILED_STATE;
      if (s === 'probing') return PROBING_STATE;
      if (s === 'ok')      return isHlsActive(row.isHlsVideo) ? PALETTES.video.active : PALETTES.video.avail;
      return EMPTY;
    }
    if (row.slot?.type === 'url') {
      const s = hlsProbeStatusMap.get(row.slot.id);
      if (s === 'failed')  return FAILED_STATE;
      if (s === 'probing') return PROBING_STATE;
      if (s === 'ok')      return selectedVideoSource === row.slot.id && !activeLayoutId ? PALETTES.video.active : PALETTES.video.avail;
      return EMPTY;
    }
    return stateOfRow('video', !!row.slot, !!(row.slot && row.slot.id === selectedVideoSource && !activeLayoutId));
  };

  const slotLabel = (idx: number): string | null => {
    const layout = deckSlotLayouts[idx];
    if (layout) { const alias = sourceAliases[`layout:${layout.id}`]; return cleanLabel(alias || layout.label); }
    const row = videoRow[idx];
    if (!row) return null;
    if (row.slot)        return cleanLabel(resolveSourceLabel(row.slot, sourceAliases));
    if (row.isHlsVideo)  return cleanLabel(sourceAliases[`hls-video-${channelPublicRef}`] || 'Playlist');
    if (row.isHlsLive)   return cleanLabel(sourceAliases[`hls-live-${channelPublicRef}`]  || 'OBS, RTMP...');
    return null;
  };

  const slotTitle = (idx: number): string => {
    const layout = deckSlotLayouts[idx];
    if (layout) { const hasContent = layout.background.type !== 'none' || layout.incrustations.length > 0; return hasContent ? `[${idx+1}] ${slotLabel(idx)}` : `Scène ${idx+1} — vide`; }
    const row = videoRow[idx];
    if (!row) return `Slot ${idx+1} — vide`;
    const label = slotLabel(idx);
    const hlsTitle = (name: string, sourceId: string, s?: HlsProbeStatus) => {
      if (s === 'ok') return `[${idx+1}] ${label}`;
      const known = hlsWasEverOk(sourceId);
      if (s === 'probing') return known ? `${name} — reprise… (cliquer pour relancer)` : `${name} — vérification…`;
      return known ? `${name} — interrompue (cliquer pour relancer)` : `${name} — indisponible`;
    };
    if (row.isHlsVideo) return hlsTitle('Playlist',      `hls-video-${channelPublicRef}`, hlsStatus(true));
    if (row.isHlsLive)  return hlsTitle('OBS, RTMP...',  `hls-live-${channelPublicRef}`,  hlsStatus(false));
    if (row.slot?.type === 'url') { const s = hlsProbeStatusMap.get(row.slot.id); if (s==='failed') return `${label||row.slot.label} — indisponible`; if (s==='probing') return `${label||row.slot.label} — vérification…`; }
    return row.slot ? `[${idx+1}] ${label}` : `Slot ${idx+1} — vide`;
  };

  const slotDisabled = (idx: number): boolean => {
    const srcId = srcIdForSlot(idx);
    if (poweredOffSlots.has(srcId)) return true;
    const layout = deckSlotLayouts[idx];
    if (layout) return layout.background.type === 'none' && layout.incrustations.length === 0;
    const row = videoRow[idx];
    if (!row) return true;
    // Un slot dont la source a DÉJÀ répondu dans la session reste cliquable même
    // en 'probing'/'failed' : le clic relance alors la sonde (cf. handleSlotClick).
    // C'est la différence entre « cette source n'existe pas encore » (première
    // connexion, rien à proposer) et « cette source a un hoquet » (reconnexion,
    // où laisser le bouton inerte prive la régie de son seul moyen d'agir).
    if (row.isHlsVideo) return hlsStatus(true)  !== 'ok' && !hlsWasEverOk(`hls-video-${channelPublicRef}`);
    if (row.isHlsLive)  return hlsStatus(false) !== 'ok' && !hlsWasEverOk(`hls-live-${channelPublicRef}`);
    if (row.slot?.type === 'url') return hlsProbeStatusMap.get(row.slot.id) !== 'ok' && !hlsWasEverOk(row.slot.id);
    return !row.slot;
  };

  const handleSlotClick = (idx: number) => {
    const layout = deckSlotLayouts[idx];
    if (layout) { onToggleLayout(layout); return; }
    if (activeLayoutId) { const l = layoutSlots.find(l => l.id === activeLayoutId); if (l) onToggleLayout(l); }
    const row = videoRow[idx];
    if (!row) return;

    // Source connue mais pas prête à l'instant : le clic RELANCE la sonde au lieu
    // de tenter une bascule vers un manifeste qui ne répond pas. Sans ça, le slot
    // rendu cliquable ci-dessus enverrait la régie sur une source morte.
    const retryIfNotReady = (sourceId: string, url: string, status?: HlsProbeStatus) => {
      if (status === 'ok') return false;
      onRetryHls(sourceId, url);
      return true;
    };

    if (row.slot) {
      if (row.slot.type === 'url' && row.slot.externalUrl &&
          retryIfNotReady(row.slot.id, row.slot.externalUrl, hlsProbeStatusMap.get(row.slot.id))) return;
      onDeckSwitch(row.slot, row.slot.type === 'screen');
      return;
    }
    if (row.isHlsVideo) {
      const src = buildHls(true);
      if (retryIfNotReady(src.id, src.externalUrl!, hlsStatus(true))) return;
      onDeckSwitch(src);
      return;
    }
    if (row.isHlsLive) {
      const src = buildHls(false);
      if (retryIfNotReady(src.id, src.externalUrl!, hlsStatus(false))) return;
      onDeckSwitch(src);
      return;
    }
  };

  const resolvePreviewSource = (idx: number): VideoSource | null => {
    const row = videoRow[idx];
    if (!row) return null;
    if (row.isHlsVideo) return row.slot ?? buildHls(true);
    if (row.isHlsLive)  return row.slot ?? buildHls(false);
    return row.slot ?? null;
  };

  const previewSlotState = (idx: number): ColorState => {
    const srcId = srcIdForSlot(idx);
    if (poweredOffSlots.has(srcId)) return EMPTY;
    const layout = deckSlotLayouts[idx];
    if (layout) {
      const hasContent = layout.background.type !== 'none' || layout.incrustations.length > 0;
      if (!hasContent) return EMPTY;
      return activeLayoutPreviewId === layout.id ? PALETTES.preview.active : PALETTES.preview.avail;
    }
    const row = videoRow[idx];
    if (!row) return EMPTY;
    if (row.isHlsVideo || row.isHlsLive) {
      const s = hlsStatus(row.isHlsVideo);
      if (s !== 'ok') return EMPTY;
      const src = row.slot ?? buildHls(row.isHlsVideo);
      return deckPreviewSourceId === src.id ? PALETTES.preview.active : PALETTES.preview.avail;
    }
    if (row.slot?.type === 'url') {
      const s = hlsProbeStatusMap.get(row.slot.id);
      if (s !== 'ok') return EMPTY;
      return deckPreviewSourceId === row.slot.id ? PALETTES.preview.active : PALETTES.preview.avail;
    }
    const src = resolvePreviewSource(idx);
    if (!src) return EMPTY;
    return deckPreviewSourceId === src.id ? PALETTES.preview.active : PALETTES.preview.avail;
  };

  const handlePreviewClick = (idx: number) => {
    const layout = deckSlotLayouts[idx];
    if (layout) { if (layout.background.type !== 'none' || layout.incrustations.length > 0) onDeckPreviewLayout(layout); return; }
    const src = resolvePreviewSource(idx);
    if (src) onDeckPreview(src);
  };

  const cutEnabled  = !!deckPreviewSourceId || !!activeLayoutPreviewId;
  const cutKeyLabel = keyMap.cut?.[0] ? formatKeyBinding(keyMap.cut[0]) : null;

  const handlePowerOffRequest = useCallback((sourceId: string, isOff: boolean, source: VideoSource | null, row: VideoRow) => {
    if (isOff) { onSlotPowerToggle(sourceId, true); return; }
    const isInFinal      = row.slot && row.slot.id === selectedVideoSource && !activeLayoutId;
    const isInPreview    = deckPreviewSourceId === sourceId;
    const sceneInFinal   = activeLayoutId        ? layoutSlots.find(l => l.id === activeLayoutId)?.incrustations.some(i => i.sourceId === sourceId) : false;
    const sceneInPreview = activeLayoutPreviewId ? layoutSlots.find(l => l.id === activeLayoutPreviewId)?.incrustations.some(i => i.sourceId === sourceId) : false;
    const isRemote       = source?.type === 'remote';
    const needsConfirm   = isInFinal || isInPreview || sceneInFinal || sceneInPreview || isRemote;
    if (!needsConfirm) { onSlotPowerToggle(sourceId, false); return; }
    let msg = 'Voulez-vous vraiment éteindre cette source ?';
    if (isInFinal || sceneInFinal)       msg = "Cette source est utilisée dans le flux final. Êtes-vous sûr de vouloir l'éteindre ?";
    if (isInPreview || sceneInPreview)   msg = "Cette source est en preview. Êtes-vous sûr de vouloir l'éteindre ?";
    if (isRemote)                        msg = "Éteindre une source distante coupera la connexion WebRTC. Confirmer ?";
    setPendingPowerOff({ sourceId, message: msg });
  }, [selectedVideoSource, activeLayoutId, activeLayoutPreviewId, deckPreviewSourceId, layoutSlots, onSlotPowerToggle]);

  const cfgHlsStatus = ((): HlsProbeStatus | undefined => {
    if (configSlotIndex === null) return undefined;
    const row = videoRow[configSlotIndex];
    if (!row) return undefined;
    if (row.isHlsVideo) return hlsProbeStatusMap.get(`hls-video-${channelPublicRef}`);
    if (row.isHlsLive)  return hlsProbeStatusMap.get(`hls-live-${channelPublicRef}`);
    if (row.slot?.type === 'url') return hlsProbeStatusMap.get(row.slot.id);
    return undefined;
  })();

  const cfgOnRetryHls = ((): (() => void) | undefined => {
    if (!cfgHlsStatus || cfgHlsStatus !== 'failed' || configSlotIndex === null) return undefined;
    const row = videoRow[configSlotIndex];
    if (!row) return undefined;
    if (row.isHlsVideo) return () => onRetryHls(`hls-video-${channelPublicRef}`, hlsUrl(true));
    if (row.isHlsLive)  return () => onRetryHls(`hls-live-${channelPublicRef}`, hlsUrl(false));
    if (row.slot?.type === 'url' && row.slot.externalUrl) {
      const id = row.slot.id; const url = row.slot.externalUrl;
      return () => onRetryHls(id, url);
    }
    return undefined;
  })();

  // ─────────────────────────────────────────────────────────────────────────
  // LAYOUT
  // Grille : TOTAL_COLS = 18 = 13 colonnes de boutons + 5 colonnes Mixeur
  // Ligne 1 : 8 vidéo   | 1 tbar | 4 médias (×2 rangées) | Mixeur
  // Ligne 2 : 8 preview | 1 cut  | 4 scènes              | (Mixeur, suite)
  // ─────────────────────────────────────────────────────────────────────────

  const gridCols = `repeat(${BUTTON_COLS}, 1fr)`;

  // Voies du bloc Mixeur : les emplacements occupés par une source (pas les
  // scènes, qui n'ont pas de son propre), dans l'ordre du deck.
  const mixerStrips: DeckMixerStrip[] = [];
  for (let idx = 0; idx < V2_VIDEO_VISIBLE; idx++) {
    if (deckSlotLayouts[idx]) continue;
    const row = videoRow[idx];
    if (!row || !(row.slot || row.isHlsVideo || row.isHlsLive)) continue;
    mixerStrips.push({ sourceId: srcIdForSlot(idx), label: slotLabel(idx) || `Source ${idx + 1}` });
  }



const getMediaBtnSlot = (a: MediaAssignment | null) => {
  if (!a) return null;
  const arr = a.kind === 'image' ? imageSlots : a.kind === 'sound' ? soundSlots : jingleSlots;
  return (arr as any[]).find(s => s.id === a.slotId) ?? null;
};

const getMediaBtnIsActive = (a: MediaAssignment | null): boolean => {
  if (!a) return false;
  if (a.kind === 'image')  return activeImageIds.has(a.slotId);
  if (a.kind === 'sound')  return activeSoundIds.has(a.slotId);
  return activeJingleId === a.slotId;
};

const handleMediaBtnClick = (btnIdx: number) => {
  const a = mediaAssignments[btnIdx]; if (!a) return;
  const slot = getMediaBtnSlot(a); if (!slot?.url) return;
  if (a.kind === 'image')  onToggleImage(a.slotId);
  else if (a.kind === 'sound')  onTriggerSound(slot as SoundSlot);
  else                          onTriggerJingle(slot as JingleSlot);
};

// Le raccourci suit le slot affecté au bouton, pas la position du bouton :
// c'est ainsi que useLiveStreamSetup les déclenche (keyMap.image[i] → imageSlots[i]).
const getMediaBtnKeyBinding = (btnIdx: number): string | null => {
  const a = mediaAssignments[btnIdx];
  if (!a) return null;
  const arr  = a.kind === 'image' ? imageSlots : a.kind === 'sound' ? soundSlots : jingleSlots;
  const i    = (arr as any[]).findIndex(s => s.id === a.slotId);
  const keys = a.kind === 'image' ? keyMap.image : a.kind === 'sound' ? keyMap.sound : keyMap.jingle;
  const kb: KeyBinding | null = i >= 0 ? (keys?.[i] ?? null) : null;
  return kb ? formatKeyBinding(kb) : null;
};

  // ── Stream Deck ────────────────────────────────────────────────────────────
  //
  // Les touches du Stream Deck reflètent les boutons de ce pupitre, rangée par
  // rangée et dans le même ordre (8 sources, 8 preview, 4 scènes, 8 médias) :
  // l'état est lu dans les mêmes fonctions que celles qui colorent l'écran, et
  // un appui passe par le même gestionnaire qu'un clic.
  const [streamDeckEnabled, setStreamDeckEnabled] = useState(loadStreamDeckEnabled);
  // Sondages en direct : publiés par PollsDock, joints à l'instantané.
  const streamDeckPolls = useStreamDeckPolls();
  // Commandes de la barre de la régie (On air, Rec…) : publiées par leurs boutons.
  const streamDeckControls = useStreamDeckControls();

  const keyStateOf = (s: ColorState, off = false): StreamDeckKeyState =>
    off                                           ? 'off'
    : s === EMPTY                                 ? 'empty'
    : s === PROBING_STATE                         ? 'probing'
    : s === FAILED_STATE                          ? 'failed'
    : Object.values(PALETTES).some(p => p.active === s) ? 'active'
    : 'avail';

  const programIdx = Array.from({ length: V2_VIDEO_VISIBLE }, (_, i) => i)
    .find(i => slotState(i) === PALETTES.video.active);
  const previewIdx = Array.from({ length: V2_VIDEO_VISIBLE }, (_, i) => i)
    .find(i => previewSlotState(i) === PALETTES.preview.active);
  const sceneOnAir = activeLayoutId ? layoutSlots.find(l => l.id === activeLayoutId) : undefined;
  const sceneInPvw = activeLayoutPreviewId ? layoutSlots.find(l => l.id === activeLayoutPreviewId) : undefined;
  const liveTbar   = tbarValueRef?.current ?? tbarValue;

  const streamDeckSnapshot: StreamDeckSnapshot = {
    holder:  isHolder,
    powered: isRegiePowered,
    channel: channelPublicRef ?? null,
    program: Array.from({ length: V2_VIDEO_VISIBLE }, (_, i) => ({
      label: slotLabel(i),
      state: keyStateOf(slotState(i), poweredOffSlots.has(srcIdForSlot(i))),
    })),
    preview: Array.from({ length: V2_VIDEO_VISIBLE }, (_, i) => ({
      label: slotLabel(i),
      state: keyStateOf(previewSlotState(i), poweredOffSlots.has(srcIdForSlot(i))),
    })),
    scenes: layoutSlots.slice(0, V2_LAYOUT_VISIBLE).map(slot => {
      const hasContent = slot.background.type !== 'none' || slot.incrustations.length > 0;
      return {
        label: hasContent ? cleanLabel(slot.label) : null,
        state: !hasContent ? 'empty' : activeLayoutId === slot.id ? 'active' : 'avail',
      } as const;
    }),
    media: Array.from({ length: TOTAL_MEDIA_BTNS }, (_, i) => {
      const a    = mediaAssignments[i] ?? null;
      const slot = getMediaBtnSlot(a);
      if (!a || !slot?.url) return { label: null, kind: null, state: 'empty' as const };
      return {
        label: cleanLabel(slot.label) || null,
        kind:  a.kind,
        state: getMediaBtnIsActive(a) ? 'active' as const : 'avail' as const,
      };
    }),
    cutEnabled: cutEnabled,
    autoActive: tbarAutoActive,
    // Arrondi au cinquantième : pendant une transition, la T-bar bouge à chaque
    // image, et le plugin n'a pas à redessiner sa molette soixante fois par seconde.
    tbar: Math.round(liveTbar * 50) / 50,
    tbarBaseline,
    onAirLabel:     sceneOnAir ? cleanLabel(sceneOnAir.label) : programIdx !== undefined ? slotLabel(programIdx) : null,
    inPreviewLabel: sceneInPvw ? cleanLabel(sceneInPvw.label) : previewIdx !== undefined ? slotLabel(previewIdx) : null,
    polls: streamDeckPolls ?? { available: false, busy: false, drafts: [], current: null },
    controls: streamDeckControls,
  };

  const handleStreamDeckCommand = (cmd: StreamDeckCommand) => {
    // Les sondages vivent sur le serveur, pas dans le pupitre : ils répondent
    // régie éteinte ou en observation, comme le panneau « Sondages ».
    if (cmd.type === 'poll') { runStreamDeckPollCommand(cmd); return; }
    // Les commandes de la barre suivent leurs propres règles (celles de leur
    // bouton à l'écran) : leur gestionnaire décide.
    if (cmd.type === 'control') { runStreamDeckControlCommand(cmd); return; }
    if (!isRegiePowered) return;
    // Une régie qui observe ne peut toucher qu'à SA preview, comme à la souris
    // (seule la rangée preview reste cliquable sous le voile).
    const previewOnly = cmd.type === 'previewStep' || (cmd.type === 'press' && cmd.target === 'preview');
    if (!isHolder && !previewOnly) return;

    if (cmd.type === 'tbar') {
      if (!onTbarChange || tbarAutoActive) return;
      const current = tbarValueRef?.current ?? tbarValue;
      onTbarChange(Math.min(1, Math.max(0, current + cmd.delta)));
      return;
    }
    if (cmd.type === 'previewStep') {
      const usable = Array.from({ length: V2_VIDEO_VISIBLE }, (_, i) => i)
        .filter(i => previewSlotState(i) !== EMPTY);
      if (!usable.length) return;
      const from = previewIdx !== undefined ? usable.indexOf(previewIdx) : -1;
      const step = cmd.delta > 0 ? 1 : -1;
      const next = from < 0
        ? (step > 0 ? 0 : usable.length - 1)
        : (from + step + usable.length) % usable.length;
      handlePreviewClick(usable[next]);
      return;
    }
    switch (cmd.target) {
      case 'program':
        if (cmd.index >= 0 && cmd.index < V2_VIDEO_VISIBLE && !slotDisabled(cmd.index)) handleSlotClick(cmd.index);
        return;
      case 'preview':
        if (cmd.index >= 0 && cmd.index < V2_VIDEO_VISIBLE && previewSlotState(cmd.index) !== EMPTY) handlePreviewClick(cmd.index);
        return;
      case 'scene': {
        const slot = layoutSlots.slice(0, V2_LAYOUT_VISIBLE)[cmd.index];
        if (slot && (slot.background.type !== 'none' || slot.incrustations.length > 0)) onToggleLayout(slot);
        return;
      }
      case 'media':
        if (cmd.index >= 0 && cmd.index < TOTAL_MEDIA_BTNS) handleMediaBtnClick(cmd.index);
        return;
      case 'cut':
        if (cutEnabled) handleCut();
        return;
      case 'auto':
        if (onTbarAuto && !tbarAutoActive) onTbarAuto();
        return;
    }
  };

  const streamDeckStatus = useStreamDeckBridge(streamDeckEnabled, streamDeckSnapshot, handleStreamDeckCommand);

  const toggleStreamDeck = () => {
    setStreamDeckEnabled(prev => { saveStreamDeckEnabled(!prev); return !prev; });
  };

  return (
    <div style={{ marginTop: 8 }}>
      {/* Corps du deck — pas de header/titre */}
      <div
        className="rounded-xl border shadow-inner"
        style={{
          padding: 10,
          opacity: isRegiePowered ? 1 : 0.35,
          pointerEvents: isRegiePowered ? 'auto' : 'none',
          transition: 'opacity 0.2s',
		  background: 'var(--color-Panel-niv1-BG)',
		  borderColor: 'var(--color-Panel-niv1-Border)'
        }}
      >

        {/* ══ Boutons (13 colonnes) | Mixeur (5 colonnes, sur les deux lignes) ══ */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${TOTAL_COLS}, 1fr)`, gap: 5, alignItems: 'stretch' }}>
        <div style={{ gridColumn: `1 / span ${BUTTON_COLS}`, minWidth: 0 }}>

        {/* ══ En-tête ligne 1 ══ */}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 5, marginBottom: 4 }}>
          <div style={{ gridColumn: `1 / span ${V2_VIDEO_VISIBLE}` }}>
            <RowLabel icon={<Video className="w-3 h-3" />} style={{ marginBottom: 0 }}>
              Source vidéo
            </RowLabel>
          </div>
          <div style={{ gridColumn: `${V2_VIDEO_VISIBLE + 1} / span 1` }} />
          <div style={{ gridColumn: `${V2_VIDEO_VISIBLE + 2} / span ${V2_MEDIA_COLS}` }}>
            <RowLabel icon={<Image className="w-3 h-3" />} style={{ marginBottom: 0 }}>
              Média
            </RowLabel>
          </div>
        </div>

        {/* ══ Ligne 1 : Sources + T-bar + Médias (double rangée) ══ */}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 5, alignItems: 'start' }}>

          {/* 8 boutons sources vidéo */}
          {Array.from({ length: V2_VIDEO_VISIBLE }, (_, idx) => {
            const srcId    = srcIdForSlot(idx);
            const isOff    = poweredOffSlots.has(srcId);
            const row      = videoRow[idx];
            const hasSource = !!(row && (row.slot || row.isHlsVideo || row.isHlsLive)) || !!deckSlotLayouts[idx];

            let onRetry: (() => void) | undefined;
            if (!isOff && row) {
              if (row.isHlsVideo && hlsProbeStatusMap.get(`hls-video-${channelPublicRef}`) === 'failed')
                onRetry = () => onRetryHls(`hls-video-${channelPublicRef}`, hlsUrl(true));
              else if (row.isHlsLive && hlsProbeStatusMap.get(`hls-live-${channelPublicRef}`) === 'failed')
                onRetry = () => onRetryHls(`hls-live-${channelPublicRef}`, hlsUrl(false));
              else if (row.slot?.type === 'url' && row.slot.externalUrl && hlsProbeStatusMap.get(row.slot.id) === 'failed') {
                const id = row.slot.id; const url = row.slot.externalUrl;
                onRetry = () => onRetryHls(id, url);
              }
            }

            return (
              <VideoDeckButton key={idx}
                label={slotLabel(idx)}
                state={slotState(idx)}
                isActive={!isOff && (
                  deckSlotLayouts[idx]
                    ? activeLayoutId === deckSlotLayouts[idx]!.id
                    : !!(row?.slot && row.slot.id === selectedVideoSource && !activeLayoutId)
                )}
                disabled={slotDisabled(idx)}
                keyBinding={keyMap.video[idx] ? formatKeyBinding(keyMap.video[idx]) : null}
                title={slotTitle(idx)}
                onClick={() => handleSlotClick(idx)}
                sourceId={srcId}
                audioCfg={getSourceAudioConfig(srcId)}
                onMuteToggle={() => onSourceMuteToggle(srcId)}
                onOpenConfig={() => handleOpenSourceConfig(idx)}
                onRetry={onRetry}
                isPoweredOff={isOff}
                onTogglePower={hasSource
                  ? () => handlePowerOffRequest(srcId, isOff, row?.slot ?? null, row || { slot:null, badge:'', isHlsVideo:false, isHlsLive:false })
                  : undefined}
                height={BTN_H1_VIDEO}
                dragProps={onSlotsReorder ? makeReorderDragHandlers({
                  family : 'slot',
                  index  : idx,
                  canDrag: hasSource,
                  onReorder: onSlotsReorder,
                  onDragStateChange: makeDragState('slot'),
                }) : undefined}
                isDragged={isDraggedIn('slot', idx)}
                isDropTarget={isDropTargetIn('slot', idx)}
              />
            );
          })}

          {/* T-bar + rangée AUTO/engrenage */}
{/* T-bar + Rangée intermédiaire */}
<div style={{
  display: 'flex', flexDirection: 'column',
  alignItems: 'stretch',
  gap: MEDIA_GAP,
  height: BTN_H1_VIDEO,
}}>

  {/* ── T-bar — agrandi (récupère l'espace du bouton Transition supprimé) ── */}
  <div style={{ flex: `0 0 ${BTN_TBAR_H}px` }}>
    <TBarFader
      value={tbarValue}
      liveValueRef={tbarValueRef}
      baseline={tbarBaseline}
      onChange={v => onTbarChange && onTbarChange(v)}
      disabled={!onTbarChange}
      height={BTN_TBAR_H}
    />
  </div>

  {/* ── AUTO + engrenage (réglages T-bar) ── */}
  <div style={{
    flex: `0 0 ${BTN_TBAR_ROW}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  }}>
    <div style={{
      width: 'calc(100% - 12px)',
      height: '100%',
      display: 'flex',
      alignItems: 'stretch',
      gap: 3,
    }}>

      <button
        onClick={() => onTbarAuto?.()}
        disabled={!onTbarAuto || tbarAutoActive}
        style={{
          flex: 1,
          background: tbarAutoActive
            ? 'linear-gradient(135deg, #166534, #22c55e)'
            : 'linear-gradient(135deg, #0f172a, #1e293b)',
          border: tbarAutoActive ? '1px solid #4ade80' : '1px solid rgba(255,255,255,0.18)',
          borderRadius: 5, cursor: onTbarAuto ? 'pointer' : 'default',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
          padding: 0,
        }}
        onMouseEnter={e => {
          if (tbarAutoActive) return;
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.35)';
          (e.currentTarget as HTMLButtonElement).style.background  = 'linear-gradient(135deg,#1e293b,#334155)';
        }}
        onMouseLeave={e => {
          if (tbarAutoActive) return;
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.18)';
          (e.currentTarget as HTMLButtonElement).style.background  = 'linear-gradient(135deg,#0f172a,#1e293b)';
        }}
        title="AUTO"
      >
        <span style={{
          fontSize: 9, fontWeight: 800, fontFamily: 'monospace',
          color: 'rgba(255,255,255,0.65)',
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>AUTO</span>
      </button>

      <button
        onClick={() => setTbarSettingsOpen(true)}
        style={{
          flex: `0 0 ${BTN_TBAR_ROW}px`,
          background: 'linear-gradient(135deg, #1e293b, #334155)',
          border: '1px solid rgba(255,255,255,0.15)',
          borderRadius: 5, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s',
          boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.4)',
          padding: 0,
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.35)';
          (e.currentTarget as HTMLButtonElement).style.background  = 'linear-gradient(135deg,#334155,#475569)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.15)';
          (e.currentTarget as HTMLButtonElement).style.background  = 'linear-gradient(135deg,#1e293b,#334155)';
        }}
        title="Réglages T-bar (vitesse, enchaînement)"
      >
        <Settings style={{ width: 14, height: 14, color: 'rgba(255,255,255,0.85)' }} />
      </button>

    </div>
  </div>

</div>


          {/* ── Zone Médias : 2 lignes × 4 colonnes ── */}
<div style={{
  gridColumn: `${V2_VIDEO_VISIBLE + 2} / span ${V2_MEDIA_COLS}`,
  display: 'grid',
  gridTemplateColumns: `repeat(${V2_MEDIA_COLS}, 1fr)`,
  gridTemplateRows: `${BTN_H2}px ${BTN_H2}px`,
  gap: 5,
}}>
  {Array.from({ length: TOTAL_MEDIA_BTNS }, (_, btnIdx) => {
    const a       = mediaAssignments[btnIdx] ?? null;
    const slot    = getMediaBtnSlot(a);
    const hasContent = !!(slot?.url);
    const isActive   = getMediaBtnIsActive(a);
    const kind       = a?.kind ?? 'image';
    const extra      = (a?.kind === 'jingle' && (slot as any)?.useCanvas)
      ? <span style={{ fontSize:7, opacity:0.5, color:'#fff' }}>[canvas]</span>
      : null;

    return (
      <DeckButton
        key={btnIdx}
        label={hasContent ? (slot?.label ?? null) : null}
        isActive={isActive}
        state={hasContent ? stateOfRow(kind, true, isActive) : EMPTY}
        disabled={!hasContent}
        keyBinding={getMediaBtnKeyBinding(btnIdx)}
        title={hasContent ? cleanLabel(slot?.label ?? '') : `Média ${btnIdx + 1}`}
        onClick={() => handleMediaBtnClick(btnIdx)}
        height={BTN_H2}
        onOpenConfig={() => setMediaConfigBtnIdx(btnIdx)}
        onAddNew={!hasContent ? () => setMediaAddPicker(true) : undefined}
        extra={extra}
        dragProps={makeReorderDragHandlers({
          family : 'media',
          index  : btnIdx,
          canDrag: hasContent,
          onReorder: handleMediaReorder,
          onDragStateChange: makeDragState('media'),
        })}
        isDragged={isDraggedIn('media', btnIdx)}
        isDropTarget={isDropTargetIn('media', btnIdx)}
      />
    );
  })}
</div>
		  
		  
		  
        </div>







        {/* ══ En-tête ligne 2 ══ */}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 5, marginBottom: 4, marginTop: 8 }}>
          <div style={{ gridColumn: `1 / span ${V2_VIDEO_VISIBLE}` }}>
            <RowLabel icon={<Eye className="w-3 h-3" />} style={{ marginBottom: 0 }}>
              Preview source
            </RowLabel>
          </div>
          <div style={{ gridColumn: `${V2_VIDEO_VISIBLE + 1} / span 1` }} />
          <div style={{ gridColumn: `${V2_VIDEO_VISIBLE + 2} / span ${SCENE_COLS}` }}>
            <RowLabel icon={<Clapperboard className="w-3 h-3" />} style={{ marginBottom: 0 }}>
              Scène (source vidéo personnalisée)
            </RowLabel>
          </div>
        </div>

        {/* ══ Ligne 2 : Previews + CUT + Scènes ══ */}
        <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: 5, alignItems: 'stretch' }}>

          {/* 8 boutons Preview */}
          {Array.from({ length: V2_VIDEO_VISIBLE }, (_, idx) => (
            <DeckButton key={idx}
              isActive={!poweredOffSlots.has(srcIdForSlot(idx)) && (
                deckSlotLayouts[idx]
                  ? activeLayoutPreviewId === deckSlotLayouts[idx]!.id
                  : deckPreviewSourceId === (resolvePreviewSource(idx)?.id ?? null)
              )}
              label={slotLabel(idx)}
              state={previewSlotState(idx)}
              disabled={slotDisabled(idx)}
              title={`Aperçu : ${slotLabel(idx) || `Slot ${idx+1} — vide`}`}
              keyBinding={keyMap.preview?.[idx] ? formatKeyBinding(keyMap.preview[idx]) : null}
              onClick={() => handlePreviewClick(idx)}
              height={BTN_H2}
              // Une régie en observation garde la prévisualisation : le flux est
              // tiré par son propre navigateur et ne touche à rien. Le CUT, la
              // ligne PGM et la configuration des emplacements restent inertes.
              interactive={!isHolder}
              // Même famille que la ligne 1 et que les mini-vues : les trois
              // rangées montrent les mêmes emplacements, on peut donc glisser de
              // l'une à l'autre, et un même échange les met toutes à jour.
              dragProps={onSlotsReorder ? makeReorderDragHandlers({
                family : 'slot',
                index  : idx,
                canDrag: !!(deckSlots[idx] || deckSlotLayouts[idx]),
                onReorder: onSlotsReorder,
                onDragStateChange: makeDragState('slot'),
              }) : undefined}
              isDragged={isDraggedIn('slot', idx)}
              isDropTarget={isDropTargetIn('slot', idx)}
            />
          ))}

{/* Bouton CUT */}
<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'stretch' }}>
  <button onClick={handleCut} disabled={!cutEnabled}
    title={cutEnabled ? `CUT${cutKeyLabel ? ` [${cutKeyLabel}]` : ''}` : 'Sélectionnez une source en preview'}
    style={{
      width: 'calc(100% - 12px)',
      height: BTN_H2,
      background: cutFlash ? 'linear-gradient(135deg,#dc2626,#ef4444)' : !cutEnabled ? '#1e293b' : 'linear-gradient(135deg,#7f1d1d,#991b1b)',
      border: `2px solid ${cutFlash ? '#fca5a5' : !cutEnabled ? '#334155' : '#ef4444'}`,
      borderRadius: 8, cursor: !cutEnabled ? 'not-allowed' : 'pointer',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4,
      transition: 'all 0.15s',
      boxShadow: cutFlash ? '0 0 16px #ef444480, inset 0 2px 4px rgba(0,0,0,0.4)' : 'inset 0 2px 4px rgba(0,0,0,0.4)',
      opacity: !cutEnabled ? 0.4 : 1, padding: 0, minWidth: 0,
    }}>
    <div style={{ width:7, height:7, borderRadius:'50%', background: cutFlash ? '#fff' : !cutEnabled ? '#334155' : '#fca5a5', boxShadow: cutFlash ? '0 0 8px #fff' : 'none' }} />
    <span style={{ fontSize:11, fontWeight:800, fontFamily:'monospace', color:'#fff', lineHeight:1 }}>CUT</span>
    {cutKeyLabel && <span style={{ fontSize:8, opacity:0.65, fontFamily:'monospace', color:'#fff', lineHeight:1 }}>[{cutKeyLabel}]</span>}
  </button>
</div>

          {/* 4 boutons Scènes (V2_LAYOUT_VISIBLE = 4) */}
          {(() => {
            // "+" : toujours viser la 1ère scène vide (parmi les 12), pas l'emplacement du bouton cliqué
            const firstEmptySceneIdx = layoutSlots.findIndex(
              s => s.background.type === 'none' && s.incrustations.length === 0
            );
            return layoutSlots.slice(0, V2_LAYOUT_VISIBLE).map((slot, idx) => {
              const hasContent = slot.background.type !== 'none' || slot.incrustations.length > 0;
              return (
                <DeckButton key={slot.id}
                  label={hasContent ? slot.label : null}
                  isActive={activeLayoutId === slot.id}
                  state={stateOfRow('layout', hasContent, activeLayoutId === slot.id)}
                  disabled={!hasContent}
                  keyBinding={keyMap.layout?.[idx] ? formatKeyBinding(keyMap.layout[idx]) : null}
                  title={!hasContent ? `Scène ${idx+1}` : cleanLabel(slot.label)}
                  onClick={() => onToggleLayout(slot)}
                  height={BTN_H2}
                  onOpenConfig={() => setSceneConfig(idx)}
                  onAddNew={!hasContent ? () => onOpenMediaboardConfig('layouts', firstEmptySceneIdx >= 0 ? firstEmptySceneIdx : idx) : undefined}
                  dragProps={makeReorderDragHandlers({
                    family : 'scene',
                    index  : idx,
                    canDrag: hasContent,
                    onReorder: handleSceneReorder,
                    onDragStateChange: makeDragState('scene'),
                  })}
                  isDragged={isDraggedIn('scene', idx)}
                  isDropTarget={isDropTargetIn('scene', idx)}
                />
              );
            });
          })()}

        </div>

        </div>

        {/* ══ Bloc Mixeur ══ */}
        <div style={{ gridColumn: `${BUTTON_COLS + 1} / span ${MIXER_COLS}`, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <DeckMixerPanel
            strips={mixerStrips}
            registry={audioSourceRegistry}
            getSourceAudioConfig={getSourceAudioConfig}
            stereoVolumes={stereoVolumes}
            onStereoVolumeChange={onStereoVolumeChange}
            onMuteToggle={onSourceMuteToggle}
            audioEffectsMap={audioEffectsMap}
            audioEffectConfigsMap={audioEffectConfigsMap}
            onToggleAudioEffect={onToggleAudioEffect}
            onAudioEffectConfigsChange={onAudioEffectConfigsChange}
          />
        </div>

        </div>

        {/* ── Légende ── */}
        <div className="flex items-center gap-4 mt-3 px-1 flex-wrap">
          {([
            [EMPTY.led,                  'Inutilisé / Éteint'],
            [PROBING_STATE.led,          'Vérification…'],
            [PALETTES.video.avail.led,   'Disponible'],
            [PALETTES.video.active.led,  'En final'],
            [PALETTES.preview.avail.led, 'Prévisualisable'],
            [PALETTES.preview.active.led,'En preview'],
            [PALETTES.layout.avail.led,  'Scène disponible'],
          ] as [string, string][]).map(([color, label]) => (
            <div key={label} className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ background:color, boxShadow: color !== EMPTY.led ? `0 0 4px ${color}` : 'none' }} />
              <span className="text-xs text-slate-400">{label}</span>
            </div>
          ))}

          {/* Stream Deck : activé à la demande (cf. streamDeckBridge.ts) */}
          <button
            onClick={toggleStreamDeck}
            className="ml-auto flex items-center gap-1.5"
            style={{
              background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 999,
              padding: '2px 10px', cursor: 'pointer', pointerEvents: 'auto',
            }}
            title={
              streamDeckStatus === 'connected' ? 'Stream Deck connecté — cliquer pour le déconnecter'
              : streamDeckStatus === 'searching' ? "Recherche du plugin Kleon dans l'application Stream Deck… — cliquer pour arrêter"
              : 'Piloter ce pupitre depuis un Stream Deck (plugin Kleon requis)'
            }
          >
            <div className="w-2 h-2 rounded-full" style={{
              background: streamDeckStatus === 'connected' ? '#4ade80' : streamDeckStatus === 'searching' ? '#facc15' : EMPTY.led,
              boxShadow:  streamDeckStatus === 'connected' ? '0 0 4px #4ade80' : 'none',
              animation:  streamDeckStatus === 'searching' ? 'probe-fade 1.6s ease-in-out infinite' : undefined,
            }} />
            <span className="text-xs text-slate-400">
              {streamDeckStatus === 'connected' ? 'Stream Deck connecté'
               : streamDeckStatus === 'searching' ? 'Stream Deck : recherche…'
               : 'Stream Deck'}
            </span>
          </button>
        </div>

      </div>

      {/* ── Modales média / scène ── */}

{mediaConfigBtnIdx !== null && (
  <UnifiedMediaConfigModal
    btnIndex={mediaConfigBtnIdx}
    currentAssignment={mediaAssignments[mediaConfigBtnIdx] ?? null}
	mediaAssignments={mediaAssignments} 
    imageSlots={imageSlots}
    soundSlots={soundSlots}
    jingleSlots={jingleSlots}
    keyMap={keyMap}
    onKeyMapChange={onKeyMapChange}
onAssign={assignment => {
  setMediaAssignments(prev => {
    const next = [...prev];
    // Retirer l'affectation de l'ancien bouton si même slot affecté ailleurs
    if (assignment) {
      for (let i = 0; i < next.length; i++) {
        if (i !== mediaConfigBtnIdx && next[i]?.kind === assignment.kind && next[i]?.slotId === assignment.slotId) {
          next[i] = null;
        }
      }
    }
    next[mediaConfigBtnIdx] = assignment;
    return next;
  });
}}
    onClose={() => setMediaConfigBtnIdx(null)}
  />
)}


      {sceneConfig !== null && (
        <SceneSlotConfigModal
          slotIndex={sceneConfig}
          currentSlot={layoutSlots[sceneConfig] ?? null}
          layoutSlots={layoutSlots}
          keyMap={keyMap}
          onKeyMapChange={onKeyMapChange}
          onSlotAssign={(idx, layout) => {
            if (!layout) return;
            // On réassigne la scène dans la liste layoutSlots
            const updated = layoutSlots.map((s, i) => i === idx ? layout : s);
            onLayoutChange?.(updated);
          }}
          onClose={() => setSceneConfig(null)}
        />
      )}

      {mediaAddPicker && (
        <MediaKindPickerModal
          onPick={handleMediaKindPick}
          onClose={() => setMediaAddPicker(false)}
        />
      )}

      {/* ── Modales existantes ── */}

      {tbarSettingsOpen && (
        <TbarSettingsModal
          speedSec={tbarSpeed}
          onSpeedChange={v => onTbarSpeedChange?.(v)}
          selectedEnchainementId={tbarEnchainementId}
          onSelectEnchainement={id => onTbarEnchainementChange?.(id)}
          enchainementConfig={tbarEnchainementConfig}
          onEnchainementConfigChange={config => onTbarEnchainementConfigChange?.(config)}
          onClose={() => setTbarSettingsOpen(false)}
        />
      )}

      {pupitreConfigOpen && (
        <PupitreConfigModalWrapper
          onClose={() => setPupitreConfigOpen(false)}
          videoRow={videoRow} deckSlots={deckSlots} deckSlotLayouts={deckSlotLayouts}
          availableSources={availableSources ?? []} sourceAliases={sourceAliases}
          onAliasesChange={onAliasesChange} onSlotSourceChange={onSlotSourceChange}
          onSlotLayoutChange={onSlotLayoutChange} jingleSlots={jingleSlots}
          layoutSlots={layoutSlots} imageSlots={imageSlots} soundSlots={soundSlots}
          videoOverlaySlots={videoOverlaySlots} activeJingleId={activeJingleId}
          activeLayoutId={activeLayoutId} onTriggerJingle={onTriggerJingle}
          onToggleLayout={onToggleLayout} keyMap={keyMap} onKeyMapChange={onKeyMapChange}
          onResetRegie={props.onResetRegie}
          slotLabelFn={slotLabel}
        />
      )}

{configSlotIndex !== null && (() => {
        const row              = videoRow[configSlotIndex];
        const cfgLayout        = deckSlotLayouts[configSlotIndex] ?? null;
        const cfgSource        = row?.slot ?? null;
        const cfgSourceResolved = cfgSource
          ? cfgSource
          : (row?.isHlsVideo ? buildHls(true) : row?.isHlsLive ? buildHls(false) : null);

        const deckSrcIds2 = new Set(videoRow.map(r => r.slot?.id).filter(Boolean) as string[]);
        const orderedSources: VideoSource[] = [
          ...videoRow.filter(r => r.slot).map(r => r.slot!),
          ...(availableSources ?? []).filter(s => !deckSrcIds2.has(s.id)),
        ];
        const videoRowSlots = videoRow.map(r => r.slot ?? null);

        // CORRECTIF : utiliser cfgSourceResolved.id et non cfgSource.id
        // Pour les slots HLS natifs (isHlsVideo / isHlsLive), row.slot est null
        // donc cfgSource est null et srcId était ''. cfgSourceResolved contient
        // le bon objet HLS avec le bon id (hls-video-xxx / hls-live-xxx).
        const srcId = cfgLayout
          ? cfgLayout.id
          : cfgSourceResolved
            ? cfgSourceResolved.id
            : '';

        return (
          <SourceConfigModal
            slotIndex={configSlotIndex} source={cfgSourceResolved} currentLayout={cfgLayout}
            availableSources={orderedSources} previewStreams={previewStreams} deckSlots={videoRowSlots}
            availableAudioDevices={availableAudioDevices ?? []} aliases={sourceAliases}
            onAliasChange={(sid, alias) => onAliasChange && onAliasChange(sid, alias)}
            onSlotSourceChange={onSlotSourceChange} onSlotLayoutChange={onSlotLayoutChange}
            selectedAudioDeviceId={selectedAudioDeviceIds?.get(srcId) ?? ''}
            onAudioDeviceChange={(sid, devId) => onAudioDeviceChange && onAudioDeviceChange(sid, devId)}
            meterSourceId={srcId}
            stereoVolume={stereoVolumes?.get(srcId) ?? DEFAULT_STEREO_VOLUME}
            onStereoVolumeChange={(sid, sv) => onStereoVolumeChange && onStereoVolumeChange(sid, sv)}
            audioCfg={getSourceAudioConfig(srcId)}
            onMuteToggle={sid => onSourceMuteToggle(sid)}
            audioEffects={audioEffectsMap?.get(srcId) ?? DEFAULT_AUDIO_EFFECTS}
            onToggleAudioEffect={(sid, fx) => onToggleAudioEffect && onToggleAudioEffect(sid, fx)}
            audioEffectConfigs={audioEffectConfigsMap?.get(srcId)}
            onAudioEffectConfigsChange={(sid, configs) => onAudioEffectConfigsChange && onAudioEffectConfigsChange(sid, configs)}
            videoEffectsSettings={videoEffectsSettingsMap?.get(srcId)}
            onVideoEffectsSettingsChange={(sid, settings) => onVideoEffectsSettingsChange && onVideoEffectsSettingsChange(sid, settings)}
            onAudioVisualChange={onAudioVisualChange}
            isLiveSlot={!!cfgSourceResolved && selectedVideoSource === cfgSourceResolved.id}
            onSwitchLive={onDeckSwitchSource ? src => onDeckSwitchSource(src) : undefined}
            onClose={handleCloseSourceConfig}
            jingleSlots={jingleSlots} layoutSlots={layoutSlots}
            activeJingleId={activeJingleId} activeLayoutId={activeLayoutId}
            onTriggerJingle={onTriggerJingle} onToggleLayout={onToggleLayout}
            hlsProbeStatus={cfgHlsStatus} onRetryHls={cfgOnRetryHls}
            onReloadSource={onReloadSource && srcId ? () => onReloadSource(srcId) : undefined}
            onReloadAudio={onReloadAudio && srcId ? () => onReloadAudio(srcId) : undefined}
            //onAddHlsSource={onAddHlsSource}
            keyMap={keyMap} onKeyMapChange={onKeyMapChange}
          />
        );
      })()}

      {pendingPowerOff && (
        <Window
          message={<>{pendingPowerOff.message}</>}
          messageType="question" messageImportance="important"
          onClickOnValidateButton={() => { onSlotPowerToggle(pendingPowerOff.sourceId, false); setPendingPowerOff(null); }}
          onClickOnCancelButton={() => setPendingPowerOff(null)}
        />
      )}
    </div>
  );
};

export default DeckV2;